import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import RoyaltiesPanel from "./components/RoyaltiesPanel";
import SignaturePad from "./components/SignaturePad";
import { countersignForm, bytesToBase64, downloadPdf, pilCopy } from "./utils/distributionFormPdf";
import { loadDeclarer, saveDeclarer, declarerComplete, buildBookmarklet, buildPilPayload, encodePayload, tepezLink } from "./utils/pilAutofill";
import { supabase } from "./supabase";
import roster from "./rosterData";

const F = "'Helvetica Neue', Helvetica, Arial, sans-serif";

/* ══════════════════════════════════════════════
   DESIGN TOKENS
══════════════════════════════════════════════ */
const D = {
  bg:       "#080808",
  surface:  "#0d0d0d",
  surface2: "#111",
  surface3: "#161616",
  border:   "rgba(255,255,255,0.07)",
  borderHi: "rgba(255,255,255,0.18)",
  text:     "#f0ede8",
  muted:    "rgba(240,237,232,0.4)",
  green:    "rgba(74,222,128,0.9)",
  red:      "rgba(248,113,113,0.9)",
  radius:   "8px",
  radiusSm: "6px",
  shadow:   "0 1px 3px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.3)",
  shadowLg: "0 4px 24px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.4)",
};

/* ══════════════════════════════════════════════
   SHARED UI PRIMITIVES
══════════════════════════════════════════════ */
function FieldLabel({ children }) {
  return (
    <p style={{ fontFamily: F, fontSize: "10px", fontWeight: 600, letterSpacing: "0.06em",
      color: D.muted, marginBottom: "8px", textTransform: "uppercase" }}>
      {children}
    </p>
  );
}

function Input({ value, onChange, placeholder, name, type = "text" }) {
  return (
    <input
      name={name} type={type} value={value}
      onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: "100%", boxSizing: "border-box", background: D.surface2,
        border: `1px solid ${D.border}`, color: D.text, fontFamily: F, fontSize: "13px",
        padding: "10px 14px", outline: "none", borderRadius: D.radiusSm,
        transition: "border-color 0.15s, box-shadow 0.15s" }}
      onFocus={e => { e.target.style.borderColor = "rgba(74,222,128,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(74,222,128,0.08)"; }}
      onBlur={e => { e.target.style.borderColor = D.border; e.target.style.boxShadow = "none"; }}
    />
  );
}

function NamedInput({ name, value, onChange, placeholder, type = "text" }) {
  return (
    <input
      name={name} type={type} value={value}
      onChange={onChange} placeholder={placeholder}
      style={{ width: "100%", boxSizing: "border-box", background: D.surface2,
        border: `1px solid ${D.border}`, color: D.text, fontFamily: F, fontSize: "13px",
        padding: "10px 14px", outline: "none", borderRadius: D.radiusSm,
        transition: "border-color 0.15s, box-shadow 0.15s" }}
      onFocus={e => { e.target.style.borderColor = "rgba(74,222,128,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(74,222,128,0.08)"; }}
      onBlur={e => { e.target.style.borderColor = D.border; e.target.style.boxShadow = "none"; }}
    />
  );
}

function ActionBtn({ onClick, children, danger = false, disabled = false, small = false }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display: "block", width: "100%", padding: small ? "9px 16px" : "13px 20px",
      border: `1px solid ${danger ? "rgba(248,113,113,0.4)" : D.borderHi}`,
      background: danger ? "rgba(248,113,113,0.06)" : "rgba(255,255,255,0.04)",
      color: danger ? D.red : D.text,
      fontFamily: F, fontSize: "12px", fontWeight: 600, letterSpacing: "0.04em",
      borderRadius: D.radiusSm, cursor: disabled ? "default" : "pointer",
      transition: "background 0.15s, border-color 0.15s", opacity: disabled ? 0.4 : 1,
      boxSizing: "border-box",
    }}
      onMouseOver={e => { if (!disabled) e.currentTarget.style.background = danger ? "rgba(248,113,113,0.12)" : "rgba(255,255,255,0.08)"; }}
      onMouseOut={e => { if (!disabled) e.currentTarget.style.background = danger ? "rgba(248,113,113,0.06)" : "rgba(255,255,255,0.04)"; }}
    >{children}</button>
  );
}

function GhostBtn({ onClick, href, children, danger = false }) {
  const s = {
    fontFamily: F, fontSize: "11px", fontWeight: 500, letterSpacing: "0.02em",
    padding: "6px 12px", background: "transparent", cursor: "pointer", textDecoration: "none",
    border: `1px solid ${danger ? "rgba(248,113,113,0.35)" : D.border}`,
    color: danger ? D.red : D.muted,
    borderRadius: D.radiusSm, transition: "all 0.15s",
  };
  const over = e => { e.currentTarget.style.background = danger ? "rgba(248,113,113,0.08)" : D.surface3; e.currentTarget.style.borderColor = danger ? "rgba(248,113,113,0.6)" : D.borderHi; e.currentTarget.style.color = danger ? D.red : D.text; };
  const out  = e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = danger ? "rgba(248,113,113,0.35)" : D.border; e.currentTarget.style.color = danger ? D.red : D.muted; };
  if (href) return <a href={href} target="_blank" rel="noreferrer" style={s} onMouseOver={over} onMouseOut={out}>{children}</a>;
  return <button onClick={onClick} style={s} onMouseOver={over} onMouseOut={out}>{children}</button>;
}

function SidebarNavItem({ icon, label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: "12px",
      width: "100%", padding: "10px 14px", boxSizing: "border-box",
      border: `1px solid ${active ? "rgba(74,222,128,0.3)" : "transparent"}`,
      background: active ? "rgba(74,222,128,0.08)" : "transparent",
      color: active ? D.text : D.muted,
      borderRadius: D.radiusSm, cursor: "pointer", textAlign: "left",
      transition: "all 0.15s",
    }}
      onMouseEnter={e => { if (!active) { e.currentTarget.style.background = D.surface3; e.currentTarget.style.color = D.text; } }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = D.muted; } }}
    >
      <span style={{ fontSize: "15px", lineHeight: 1, width: "18px", textAlign: "center", flexShrink: 0,
        color: active ? "rgba(74,222,128,0.9)" : "inherit" }}>{icon}</span>
      <span style={{ fontFamily: F, fontSize: "12px", fontWeight: 600, letterSpacing: "0.02em" }}>{label}</span>
    </button>
  );
}

function CommandPalette({ open, onClose, tiles, releases, artists, onNavigate, onEditRelease, onEditArtist }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setQuery(""); setTimeout(() => inputRef.current?.focus(), 10); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const panelResults = (q ? tiles.filter(t => t.label.toLowerCase().includes(q)) : tiles).slice(0, 6);
  const releaseResults = (q
    ? releases.filter(r => r.title?.toLowerCase().includes(q) || r.artist?.toLowerCase().includes(q))
    : [...releases].sort((a, b) => new Date(b.date) - new Date(a.date))
  ).slice(0, q ? 6 : 4);
  const artistResults = (q
    ? artists.filter(a => a.display_name?.toLowerCase().includes(q) || a.id?.toLowerCase().includes(q))
    : []
  ).slice(0, 5);

  const results = [
    ...panelResults.map(t => ({ type: "panel", key: `p-${t.id}`, icon: t.icon, text: t.label, run: () => { onNavigate(t.id); onClose(); } })),
    ...releaseResults.map(r => ({ type: "release", key: `r-${r.id}`, icon: "♫", text: `${r.artist} — ${r.title}`, run: () => { onEditRelease(r); onClose(); } })),
    ...artistResults.map(a => ({ type: "artist", key: `a-${a.id}`, icon: "◈", text: a.display_name, run: () => { onEditArtist(a); onClose(); } })),
  ];

  const itemStyle = {
    display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "9px 10px",
    background: "transparent", border: "none", borderRadius: D.radiusSm, cursor: "pointer",
    textAlign: "left", color: D.text, fontFamily: F, fontSize: "12px", fontWeight: 500,
  };
  const sectionLabelStyle = {
    fontFamily: F, fontSize: "9px", fontWeight: 700, letterSpacing: "0.1em",
    textTransform: "uppercase", color: D.muted, padding: "8px 10px 4px",
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 200,
      display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "12vh 16px 16px",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: "480px", background: D.surface, border: `1px solid ${D.borderHi}`,
        borderRadius: D.radius, boxShadow: D.shadowLg, overflow: "hidden",
      }}>
        <input
          ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && results[0]) results[0].run(); }}
          placeholder="Search releases, artists, or jump to a panel..."
          style={{ width: "100%", boxSizing: "border-box", background: "transparent", border: "none",
            borderBottom: `1px solid ${D.border}`, color: D.text, fontFamily: F, fontSize: "14px",
            padding: "16px 18px", outline: "none" }}
        />
        <div style={{ maxHeight: "50vh", overflowY: "auto", padding: "6px" }}>
          {panelResults.length > 0 && <p style={sectionLabelStyle}>Panels</p>}
          {panelResults.map(t => (
            <button key={`p-${t.id}`} style={itemStyle} onClick={() => { onNavigate(t.id); onClose(); }}
              onMouseEnter={e => e.currentTarget.style.background = D.surface3}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ width: "16px", textAlign: "center", flexShrink: 0 }}>{t.icon}</span>{t.label}
            </button>
          ))}
          {releaseResults.length > 0 && <p style={sectionLabelStyle}>Releases</p>}
          {releaseResults.map(r => (
            <button key={`r-${r.id}`} style={itemStyle} onClick={() => { onEditRelease(r); onClose(); }}
              onMouseEnter={e => e.currentTarget.style.background = D.surface3}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ width: "16px", textAlign: "center", flexShrink: 0 }}>♫</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.artist} — {r.title}</span>
            </button>
          ))}
          {artistResults.length > 0 && <p style={sectionLabelStyle}>Artists</p>}
          {artistResults.map(a => (
            <button key={`a-${a.id}`} style={itemStyle} onClick={() => { onEditArtist(a); onClose(); }}
              onMouseEnter={e => e.currentTarget.style.background = D.surface3}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ width: "16px", textAlign: "center", flexShrink: 0 }}>◈</span>{a.display_name}
            </button>
          ))}
          {results.length === 0 && (
            <p style={{ padding: "24px 10px", textAlign: "center", fontFamily: F, fontSize: "12px", color: D.muted }}>No matches.</p>
          )}
        </div>
        <div style={{ borderTop: `1px solid ${D.border}`, padding: "8px 14px", display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: F, fontSize: "9px", color: D.muted }}>↵ select · esc close</span>
          <span style={{ fontFamily: F, fontSize: "9px", color: D.muted }}>⌘K</span>
        </div>
      </div>
    </div>
  );
}

function StatusMsg({ status, noun }) {
  if (!status) return null;
  const ok = status === "success";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "8px",
      marginTop: "10px", padding: "10px 12px",
      background: ok ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)",
      border: `1px solid ${ok ? "rgba(74,222,128,0.2)" : "rgba(248,113,113,0.2)"}`,
      borderRadius: D.radiusSm,
    }}>
      <span style={{ fontSize: "12px" }}>{ok ? "✓" : "✕"}</span>
      <p style={{ fontFamily: F, fontSize: "12px", fontWeight: 500,
        color: ok ? D.green : D.red }}>
        {ok ? noun : status.replace("error:", "")}
      </p>
    </div>
  );
}

function DataRow({ label, sub, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "12px 14px", borderRadius: D.radiusSm, gap: "12px",
      background: D.surface, border: `1px solid ${D.border}`,
      transition: "border-color 0.15s",
    }}
      onMouseOver={e => e.currentTarget.style.borderColor = D.borderHi}
      onMouseOut={e => e.currentTarget.style.borderColor = D.border}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <p style={{ fontFamily: F, fontSize: "13px", fontWeight: 500, color: D.text,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</p>
        {sub && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted, marginTop: "2px",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</p>}
      </div>
      {children && <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>{children}</div>}
    </div>
  );
}

function FormSection({ title, hint, children }) {
  return (
    <div style={{ paddingTop: "16px", marginTop: "16px", borderTop: `1px solid ${D.border}` }}>
      <p style={{ fontFamily: F, fontSize: "9px", fontWeight: 700, letterSpacing: "0.1em",
        textTransform: "uppercase", color: "rgba(74,222,128,0.75)", marginBottom: hint ? "3px" : "10px" }}>{title}</p>
      {hint && <p style={{ fontFamily: F, fontSize: "10px", color: D.muted, marginBottom: "10px", lineHeight: 1.5 }}>{hint}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>{children}</div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div style={{
      background: D.surface2, border: `1px solid ${D.border}`, borderRadius: D.radiusSm,
      padding: "16px 12px", textAlign: "center",
    }}>
      <p style={{ fontFamily: F, fontSize: "24px", fontWeight: 700, color: D.text, lineHeight: 1 }}>{value}</p>
      <p style={{ fontFamily: F, fontSize: "9px", fontWeight: 600, letterSpacing: "0.06em",
        textTransform: "uppercase", color: D.muted, marginTop: "8px" }}>{label}</p>
    </div>
  );
}

function Panel({ children }) {
  return (
    <div style={{
      background: D.surface, border: `1px solid ${D.border}`,
      borderRadius: D.radius, padding: "20px",
      marginBottom: "10px", boxShadow: D.shadow,
    }}>
      {children}
    </div>
  );
}


/* ══════════════════════════════════════════════
   RICH TEXT EDITOR (for press posts)
══════════════════════════════════════════════ */
function RichEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [preview, setPreview] = useState(false);
  const savedRange = useRef(null);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== value) el.innerHTML = value || "";
  }, []); // eslint-disable-line

  const emit = useCallback(() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }, [onChange]);
  const saveRange = () => { const s = window.getSelection(); if (s?.rangeCount) savedRange.current = s.getRangeAt(0).cloneRange(); };
  const restoreRange = () => { const s = window.getSelection(); if (savedRange.current && s) { s.removeAllRanges(); s.addRange(savedRange.current); } };
  const exec = (cmd, val = null) => { editorRef.current?.focus(); document.execCommand(cmd, false, val); emit(); };

  const insertLink = () => {
    restoreRange();
    const url = linkUrl.trim(); if (!url) return;
    document.execCommand("insertHTML", false, `<a href="${url}" target="_blank" rel="noreferrer">${linkText.trim() || url}</a>`);
    emit(); setShowLinkDialog(false); setLinkUrl(""); setLinkText("");
  };
  const insertImage = () => {
    restoreRange();
    const url = imageUrl.trim(); if (!url) return;
    document.execCommand("insertHTML", false, `<img src="${url}" alt="" style="width:100%;display:block;margin:24px 0;" />`);
    emit(); setShowImageDialog(false); setImageUrl("");
  };

  const toolBtn = (label, action, title) => (
    <button type="button" title={title || label}
      onMouseDown={e => { e.preventDefault(); saveRange(); action(); }}
      style={{ background: "transparent", border: "none", color: "#f0ede8", fontFamily: F, fontSize: "10px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer", padding: "4px 8px", opacity: 0.6 }}>
      {label}
    </button>
  );

  const dInput = { background: "transparent", border: "none", borderBottom: "1px solid #2a2a2a", color: "#f0ede8", fontFamily: F, fontSize: "12px", letterSpacing: "0.05em", padding: "6px 2px", outline: "none", width: "100%" };
  const dBtn = { background: "transparent", border: "1px solid #333", color: "#f0ede8", fontFamily: F, fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", padding: "6px 14px", cursor: "pointer" };

  return (
    <div style={{ border: "1px solid #222", background: "#050505" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "2px", padding: "8px 10px", borderBottom: "1px solid #1a1a1a", alignItems: "center" }}>
        {toolBtn("B", () => exec("bold"), "Bold")}
        {toolBtn("I", () => exec("italic"), "Italic")}
        {toolBtn("H2", () => exec("formatBlock", "h2"), "Heading 2")}
        {toolBtn("H3", () => exec("formatBlock", "h3"), "Heading 3")}
        {toolBtn("P", () => exec("formatBlock", "p"), "Paragraph")}
        {toolBtn("—", () => exec("insertHorizontalRule"), "Divider")}
        <div style={{ width: "1px", height: "16px", background: "#222", margin: "0 4px" }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); saveRange(); setShowLinkDialog(true); setShowImageDialog(false); }}
          style={{ background: "transparent", border: "none", color: "#f0ede8", fontFamily: F, fontSize: "10px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer", padding: "4px 8px", opacity: 0.6 }}>Link</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); saveRange(); setShowImageDialog(true); setShowLinkDialog(false); }}
          style={{ background: "transparent", border: "none", color: "#f0ede8", fontFamily: F, fontSize: "10px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer", padding: "4px 8px", opacity: 0.6 }}>Img</button>
        <div style={{ marginLeft: "auto" }}>
          <button type="button" onClick={() => setPreview(p => !p)}
            style={{ background: "transparent", border: "none", color: "#f0ede8", fontFamily: F, fontSize: "10px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer", padding: "4px 8px", opacity: preview ? 1 : 0.45 }}>
            {preview ? "Edit" : "Preview"}
          </button>
        </div>
      </div>
      {showLinkDialog && (
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #1a1a1a", background: "#0a0a0a", display: "flex", flexDirection: "column", gap: "8px" }}>
          <input autoFocus placeholder="URL (https://...)" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && insertLink()} style={dInput} />
          <input placeholder="Display text (optional)" value={linkText} onChange={e => setLinkText(e.target.value)} onKeyDown={e => e.key === "Enter" && insertLink()} style={dInput} />
          <div style={{ display: "flex", gap: "8px" }}>
            <button type="button" onClick={insertLink} style={dBtn}>Insert</button>
            <button type="button" onClick={() => setShowLinkDialog(false)} style={{ ...dBtn, opacity: 0.4 }}>Cancel</button>
          </div>
        </div>
      )}
      {showImageDialog && (
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #1a1a1a", background: "#0a0a0a", display: "flex", flexDirection: "column", gap: "8px" }}>
          <input autoFocus placeholder="Image URL (https://...)" value={imageUrl} onChange={e => setImageUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && insertImage()} style={dInput} />
          <div style={{ display: "flex", gap: "8px" }}>
            <button type="button" onClick={insertImage} style={dBtn}>Insert</button>
            <button type="button" onClick={() => setShowImageDialog(false)} style={{ ...dBtn, opacity: 0.4 }}>Cancel</button>
          </div>
        </div>
      )}
      {preview ? (
        <div className="press-body" style={{ padding: "20px 24px", minHeight: "280px", fontSize: "14px", lineHeight: 1.8, color: "rgba(240,237,232,0.7)" }} dangerouslySetInnerHTML={{ __html: value }} />
      ) : (
        <div ref={editorRef} contentEditable suppressContentEditableWarning data-yen-editor onInput={emit} onBlur={emit}
          style={{ padding: "20px 24px", minHeight: "280px", outline: "none", fontFamily: F, fontSize: "14px", lineHeight: 1.8, color: "rgba(240,237,232,0.75)", caretColor: "#f0ede8" }} />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   SLUG MANAGER PANEL
══════════════════════════════════════════════ */
function SlugManager({ artists }) {
  const [slugs, setSlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableError, setTableError] = useState(null);
  const [newSlug, setNewSlug] = useState("");
  const [newDest, setNewDest] = useState("");
  const [addStatus, setAddStatus] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase.from("slugs").select("*").order("created_at", { ascending: false });
      if (error) {
        setTableError(error.message);
      } else {
        setSlugs(data || []);
      }
      setLoading(false);
    }
    load();
  }, []);

  async function addSlug() {
    const slug = newSlug.trim().toLowerCase().replace(/\s+/g, "-");
    const dest = newDest.trim();
    if (!slug || !dest) { setAddStatus("error:Slug and destination are both required."); return; }
    setAddStatus("saving");
    const { data, error } = await supabase.from("slugs").insert([{ slug, destination: dest }]).select().single();
    if (error) {
      setAddStatus("error:" + error.message);
    } else {
      setSlugs(p => [data, ...p]);
      setNewSlug(""); setNewDest("");
      setAddStatus("success");
    }
    setTimeout(() => setAddStatus(null), 3000);
  }

  async function deleteSlug(id, slug) {
    if (!window.confirm(`Delete slug "/${slug}"?`)) return;
    const { error } = await supabase.from("slugs").delete().eq("id", id);
    if (!error) setSlugs(p => p.filter(s => s.id !== id));
    else alert("Delete error: " + error.message);
  }

  function copyLink(path) {
    navigator.clipboard.writeText(`https://yensound.com/${path}`);
    setCopiedId(path); setTimeout(() => setCopiedId(null), 1500);
  }

  const artistSlugs = artists.filter(a => a.slug);

  /* Table doesn't exist yet — show SQL setup instructions */
  if (tableError) return (
    <div>
      <p style={{ fontFamily: F, fontSize: "10px", color: "rgba(255,100,100,0.85)", marginBottom: "16px", lineHeight: 1.7 }}>
        ⚠ Supabase error: {tableError}
      </p>
      <FieldLabel>Run this SQL in your Supabase dashboard → SQL Editor</FieldLabel>
      <pre style={{ background: "#080808", border: "1px solid #1a1a1a", padding: "16px", fontFamily: "monospace", fontSize: "11px", color: "rgba(100,255,180,0.85)", overflowX: "auto", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
{`CREATE TABLE slugs (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  slug text UNIQUE NOT NULL,
  destination text NOT NULL,
  created_at timestamptz DEFAULT now()
);

ALTER TABLE slugs ENABLE ROW LEVEL SECURITY;

CREATE POLICY "Public read" ON slugs
  FOR SELECT USING (true);

CREATE POLICY "Anon insert" ON slugs
  FOR INSERT WITH CHECK (true);

CREATE POLICY "Anon delete" ON slugs
  FOR DELETE USING (true);`}
      </pre>
      <p style={{ fontFamily: F, fontSize: "9px", opacity: 0.4, marginTop: "12px", letterSpacing: "0.1em" }}>
        After running, reload this page.
      </p>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>

      {/* Artist page slugs (read-only) */}
      <div style={{ marginBottom: "28px" }}>
        <FieldLabel>Artist Page Slugs — /artist/:slug</FieldLabel>
        {artistSlugs.length === 0 && (
          <p style={{ fontFamily: F, fontSize: "10px", opacity: 0.25, padding: "10px 0" }}>No artist slugs set yet.</p>
        )}
        {artistSlugs.map(a => (
          <DataRow key={a.id} label={`/artist/${a.slug}`} sub={a.display_name}>
            <GhostBtn onClick={() => copyLink(`artist/${a.slug}`)}>
              {copiedId === `artist/${a.slug}` ? "Copied" : "Copy"}
            </GhostBtn>
            <GhostBtn href={`/artist/${a.slug}`}>View</GhostBtn>
          </DataRow>
        ))}
      </div>

      {/* Custom redirect slugs */}
      <div>
        <FieldLabel>Custom Redirects — /:slug → any URL</FieldLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
          <Input value={newSlug} onChange={setNewSlug} placeholder="short path (e.g. presskit)" />
          <Input value={newDest} onChange={setNewDest} placeholder="destination (https://...)" />
          <ActionBtn onClick={addSlug} disabled={addStatus === "saving"}>
            {addStatus === "saving" ? "Saving..." : "Add Redirect"}
          </ActionBtn>
          <StatusMsg status={addStatus} noun="Redirect saved — visit /shortpath to test" />
        </div>

        {loading && <p style={{ fontFamily: F, fontSize: "10px", opacity: 0.25 }}>Loading...</p>}
        {!loading && slugs.length === 0 && (
          <p style={{ fontFamily: F, fontSize: "10px", opacity: 0.25, padding: "10px 0" }}>No custom redirects yet.</p>
        )}
        {slugs.map(s => (
          <DataRow key={s.id} label={`/${s.slug}`} sub={s.destination}>
            <GhostBtn onClick={() => copyLink(s.slug)}>
              {copiedId === s.slug ? "Copied" : "Copy"}
            </GhostBtn>
            <GhostBtn danger onClick={() => deleteSlug(s.id, s.slug)}>Delete</GhostBtn>
          </DataRow>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   CLOUDINARY PHOTO UPLOAD HELPER
   Free tier: 25 GB storage/bandwidth, no credit card.
   Setup: create a free account at cloudinary.com,
   then create an "unsigned" upload preset and paste
   your cloud_name + preset below (or use env vars).
══════════════════════════════════════════════ */

// ── configure these two values ──
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "YOUR_CLOUD_NAME";
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "YOUR_UNSIGNED_PRESET";

/**
 * Uploads a file to Cloudinary and returns a transformed URL.
 * Defaults match the original artist-photo behavior (square, face-cropped)
 * for backward compatibility — pass opts.folder / opts.transform to change
 * either for a different use case (e.g. release cover art).
 */
async function uploadToCloudinary(file, onProgress, opts) {
  const folder = (opts && opts.folder) || "yen-sound/artists";
  const transform = (opts && opts.transform) || "c_fill,g_face,w_600,h_600,f_auto,q_auto";
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  formData.append("folder", folder);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`);
    xhr.upload.onprogress = e => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        const transformedUrl = data.secure_url.replace("/upload/", `/upload/${transform}/`);
        resolve({ url: transformedUrl, publicId: data.public_id });
      } else {
        reject(new Error("Upload failed: " + xhr.responseText));
      }
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(formData);
  });
}

/* ── generic cover-image upload field (click thumbnail to upload via
   Cloudinary, or paste a URL directly) — used in the release form ── */
function CoverUploadField({ value, onChange, folder = "yen-sound/releases", label = "Cover Image" }) {
  const [preview, setPreview] = useState(value || "");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);
  const configured = CLOUDINARY_CLOUD_NAME !== "YOUR_CLOUD_NAME";

  useEffect(() => { setPreview(value || ""); }, [value]);

  async function handleFile(file) {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setUploading(true); setProgress(0);
    try {
      const { url } = await uploadToCloudinary(file, setProgress, {
        folder,
        transform: "c_fill,g_auto,w_1200,h_1200,f_auto,q_auto",
      });
      setPreview(url);
      onChange(url);
    } catch (err) {
      alert("Upload error: " + err.message);
    }
    setUploading(false);
  }

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
        <div
          style={{ width: "72px", height: "72px", flexShrink: 0, overflow: "hidden", background: D.surface2, border: `1px solid ${D.border}`, borderRadius: D.radiusSm, cursor: configured ? "pointer" : "default", position: "relative" }}
          onClick={() => configured && fileRef.current?.click()}
          title={configured ? "Click to upload" : ""}
        >
          {preview
            ? <img src={preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.2, fontFamily: F, fontSize: "9px" }}>?</div>
          }
          {uploading && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px" }}>
              <div style={{ width: "40px", height: "2px", background: "#222" }}>
                <div style={{ width: `${progress}%`, height: "100%", background: "#f0ede8", transition: "width 0.2s" }} />
              </div>
              <span style={{ fontFamily: F, fontSize: "7px", color: "#f0ede8", opacity: 0.7 }}>{progress}%</span>
            </div>
          )}
          {configured && !uploading && (
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.6)", padding: "3px 0", textAlign: "center" }}>
              <span style={{ fontFamily: F, fontSize: "6px", letterSpacing: "0.15em", color: "#f0ede8", opacity: 0.6, textTransform: "uppercase" }}>Upload</span>
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            value={value || ""}
            onChange={e => onChange(e.target.value)}
            placeholder={configured ? "Click thumbnail to upload, or paste a URL" : "Paste image URL — set Cloudinary env vars to enable upload"}
            style={{ width: "100%", boxSizing: "border-box", background: D.surface2, border: `1px solid ${D.border}`, color: D.text, fontFamily: F, fontSize: "13px", padding: "10px 14px", outline: "none", borderRadius: D.radiusSm }}
          />
          {!configured && (
            <p style={{ fontFamily: F, fontSize: "8px", opacity: 0.4, letterSpacing: "0.06em", lineHeight: 1.6, marginTop: "6px" }}>
              ⚠ Set REACT_APP_CLOUDINARY_CLOUD_NAME + REACT_APP_CLOUDINARY_UPLOAD_PRESET in .env to enable direct upload.
            </p>
          )}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleFile(e.target.files?.[0])} />
    </div>
  );
}

/* ── single-artist photo upload row ── */
function ArtistPhotoRow({ rosterEntry: r, dbArtist, currentImage, onSaved }) {
  const id = dbArtist?.id;
  const [urlInput, setUrlInput] = useState(dbArtist?.profile_image ?? "");
  const [preview, setPreview] = useState(currentImage);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const fileRef = useRef(null);

  async function handleFile(file) {
    if (!file) return;
    // Client-side preview
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setUploading(true); setProgress(0); setStatus("idle");
    try {
      const { url } = await uploadToCloudinary(file, setProgress);
      setUrlInput(url);
      setPreview(url);
      setUploading(false);
      await saveUrl(id, url, onSaved);
    } catch (err) {
      setUploading(false);
      setStatus("error");
      alert("Upload error: " + err.message);
    }
  }

  async function saveUrl(artistId, url, cb) {
    if (!artistId) return;
    setStatus("saving");
    const { error } = await supabase.from("artists").update({ profile_image: url || null }).eq("id", artistId);
    if (error) { setStatus("error"); alert("Save error: " + error.message); }
    else { setStatus("saved"); cb({ id: artistId, profile_image: url || null }); setTimeout(() => setStatus("idle"), 2500); }
  }

  async function handleRevert() {
    setUrlInput(""); setPreview(r.image);
    await saveUrl(id, null, onSaved);
  }

  const configured = CLOUDINARY_CLOUD_NAME !== "YOUR_CLOUD_NAME";

  return (
    <div style={{ display: "flex", gap: "14px", alignItems: "flex-start", padding: "16px", border: "1px solid rgba(240,237,232,0.1)", background: "#050505" }}>
      {/* square thumbnail */}
      <div
        style={{ width: "64px", height: "64px", flexShrink: 0, overflow: "hidden", background: "#111", border: "1px solid #1a1a1a", cursor: id ? "pointer" : "default", position: "relative" }}
        onClick={() => id && configured && fileRef.current?.click()}
        title={id && configured ? "Click to upload new photo" : ""}
      >
        {preview
          ? <img src={preview} alt={r.displayName} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.2, fontFamily: F, fontSize: "9px" }}>?</div>
        }
        {uploading && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px" }}>
            <div style={{ width: "36px", height: "2px", background: "#222" }}>
              <div style={{ width: `${progress}%`, height: "100%", background: "#f0ede8", transition: "width 0.2s" }} />
            </div>
            <span style={{ fontFamily: F, fontSize: "7px", letterSpacing: "0.15em", color: "#f0ede8", opacity: 0.7 }}>{progress}%</span>
          </div>
        )}
        {id && configured && !uploading && (
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.6)", padding: "3px 0", textAlign: "center" }}>
            <span style={{ fontFamily: F, fontSize: "6px", letterSpacing: "0.15em", color: "#f0ede8", opacity: 0.6, textTransform: "uppercase" }}>Upload</span>
          </div>
        )}
      </div>

      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleFile(e.target.files?.[0])} />

      {/* name + url input */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontFamily: F, fontSize: "11px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "8px" }}>
          {r.displayName}
          {dbArtist?.profile_image && <span style={{ fontFamily: F, fontSize: "8px", letterSpacing: "0.15em", opacity: 0.4, marginLeft: "8px", fontWeight: 400 }}>custom</span>}
          {!dbArtist?.profile_image && <span style={{ fontFamily: F, fontSize: "8px", letterSpacing: "0.15em", opacity: 0.25, marginLeft: "8px", fontWeight: 400 }}>from rosterData</span>}
        </p>

        {!configured && (
          <p style={{ fontFamily: F, fontSize: "8px", opacity: 0.4, letterSpacing: "0.1em", lineHeight: 1.6, marginBottom: "6px" }}>
            ⚠ Set REACT_APP_CLOUDINARY_CLOUD_NAME + REACT_APP_CLOUDINARY_UPLOAD_PRESET in .env to enable uploads. URL paste still works.
          </p>
        )}

        <div style={{ display: "flex", gap: "8px" }}>
          <input
            value={urlInput}
            onChange={e => { setUrlInput(e.target.value); setPreview(e.target.value || currentImage); }}
            placeholder={id ? "https://... or click thumbnail to upload" : "Artist not in Supabase yet"}
            disabled={!id}
            style={{ flex: 1, background: "transparent", border: "1px solid rgba(240,237,232,0.2)", color: "#f0ede8", fontFamily: F, fontSize: "11px", padding: "8px 10px", outline: "none", opacity: id ? 1 : 0.3 }}
            onFocus={e => e.target.style.borderColor = "rgba(240,237,232,0.6)"}
            onBlur={e => e.target.style.borderColor = "rgba(240,237,232,0.2)"}
          />
          {id && (
            <button
              onClick={() => saveUrl(id, urlInput.trim(), onSaved)}
              disabled={status === "saving" || uploading}
              style={{ flexShrink: 0, padding: "8px 14px", background: "transparent", border: "1px solid rgba(240,237,232,0.5)", color: "#f0ede8", fontFamily: F, fontSize: "9px", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", cursor: "pointer", opacity: (status === "saving" || uploading) ? 0.4 : 1 }}
            >
              {status === "saving" ? "..." : status === "saved" ? "✓" : "Save"}
            </button>
          )}
        </div>
        {!id && <p style={{ fontFamily: F, fontSize: "8px", opacity: 0.3, marginTop: "4px", letterSpacing: "0.1em" }}>Add this artist to Supabase to enable image override</p>}
        {id && dbArtist?.profile_image && (
          <button onClick={handleRevert} style={{ marginTop: "4px", background: "none", border: "none", color: "rgba(220,80,80,0.6)", fontFamily: F, fontSize: "8px", letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer", padding: 0 }}>
            ↺ revert to rosterData
          </button>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   PHOTOS PANEL
   Manages profile_image overrides per artist.
   Falls back to rosterData.image on the site if not set.
   Requires: ALTER TABLE artists ADD COLUMN IF NOT EXISTS profile_image text;
══════════════════════════════════════════════ */
function PhotosPanel({ artists, onUpdate }) {
  const rows = roster.map(r => {
    const dbArtist = artists.find(a => a.slug === r.slug || a.display_name?.toLowerCase() === r.displayName?.toLowerCase());
    return { rosterEntry: r, dbArtist, currentImage: dbArtist?.profile_image || r.image };
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <FieldLabel>Artist Profile Images — click thumbnail to upload (auto-squared) or paste URL</FieldLabel>
      {rows.map(({ rosterEntry: r, dbArtist, currentImage }) => (
        <ArtistPhotoRow key={r.slug || r.name} rosterEntry={r} dbArtist={dbArtist} currentImage={currentImage} onSaved={onUpdate} />
      ))}
    </div>
  );
}


/* ══════════════════════════════════════════════
   RELEASE PAGE CONFIG PANEL
   Manages custom_buttons, button_order, embed_url per release
══════════════════════════════════════════════ */
const RELEASE_PLATFORMS = ["spotify","appleMusic","youtube","tiktok","instagram","soundcloud","bandcamp","website"];
const RELEASE_PLATFORM_LABELS = {
  spotify:"Spotify", appleMusic:"Apple Music", youtube:"YouTube",
  tiktok:"TikTok", instagram:"Instagram", soundcloud:"SoundCloud",
  bandcamp:"Bandcamp", website:"Website",
};

function ReleasePageConfig({ releaseId, initialButtons, initialOrder, initialEmbedUrl, onSaved }) {
  const [buttons, setButtons] = useState(initialButtons || []);
  const [buttonOrder, setButtonOrder] = useState(
    initialOrder?.length ? initialOrder : [...RELEASE_PLATFORMS]
  );
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  // new custom button form
  const [newType, setNewType] = useState("link");
  const [newLabel, setNewLabel] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const allCustomIds = buttons.map(b => b.id);
  const fullOrder = [...buttonOrder, ...allCustomIds.filter(id => !buttonOrder.includes(id))];

  function moveUp(key) {
    const i = fullOrder.indexOf(key);
    if (i <= 0) return;
    const next = [...fullOrder];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    setButtonOrder(next);
  }
  function moveDown(key) {
    const i = fullOrder.indexOf(key);
    if (i >= fullOrder.length - 1) return;
    const next = [...fullOrder];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    setButtonOrder(next);
  }
  function removeCustom(id) {
    setButtons(b => b.filter(x => x.id !== id));
    setButtonOrder(o => o.filter(x => x !== id));
  }
  function addCustom() {
    if (!newUrl.trim()) { setStatus("error:URL is required"); return; }
    if (newType !== "embed" && !newLabel.trim()) { setStatus("error:Label is required"); return; }
    const id = `custom_${Date.now()}`;
    const btn = { id, type: newType, url: newUrl.trim(), label: newLabel.trim() };
    if (newType === "locked") btn.password = newPassword.trim();
    setButtons(b => [...b, btn]);
    setButtonOrder(o => [...o, id]);
    setNewLabel(""); setNewUrl(""); setNewPassword(""); setStatus(null);
  }

  async function save() {
    setSaving(true); setStatus(null);
    const { error } = await supabase.from("releases").update({
      custom_buttons: buttons,
      button_order: fullOrder,
      embed_url: null,
    }).eq("id", releaseId);
    if (error) setStatus("error:" + error.message);
    else { setStatus("success"); onSaved?.(); setTimeout(() => setStatus(null), 2500); }
    setSaving(false);
  }

  const selectStyle = {
    background: "#000", border: "1px solid rgba(240,237,232,0.2)", color: "#f0ede8",
    fontFamily: F, fontSize: "11px", padding: "10px 12px", cursor: "pointer",
    outline: "none", width: "100%",
  };

  return (
    <div>
      <FieldLabel>Button Order</FieldLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "20px" }}>
        {fullOrder.map((key, i) => {
          const isPlatform = RELEASE_PLATFORMS.includes(key);
          const customBtn = buttons.find(b => b.id === key);
          const label = isPlatform ? RELEASE_PLATFORM_LABELS[key] : (customBtn?.label || customBtn?.type || key);
          return (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", border: "1px solid rgba(240,237,232,0.1)", background: "#050505" }}>
              <span style={{ fontFamily: F, fontSize: "10px", flex: 1, color: "#f0ede8", opacity: isPlatform ? 0.5 : 1 }}>
                {isPlatform ? `[${label}]` : label}
                {!isPlatform && customBtn && <span style={{ opacity: 0.3, fontSize: "9px", marginLeft: "8px" }}>{customBtn.type}</span>}
              </span>
              <GhostBtn onClick={() => moveUp(key)}>↑</GhostBtn>
              <GhostBtn onClick={() => moveDown(key)}>↓</GhostBtn>
              {!isPlatform && <GhostBtn danger onClick={() => removeCustom(key)}>✕</GhostBtn>}
            </div>
          );
        })}
      </div>

      <FieldLabel>Add Custom Button</FieldLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
        <select value={newType} onChange={e => setNewType(e.target.value)} style={selectStyle}>
          <option value="link">Link</option>
          <option value="embed">Embed (Spotify / YouTube / SoundCloud)</option>
          <option value="locked">Password-Locked Link</option>
        </select>
        {newType !== "embed" && (
          <Input value={newLabel} onChange={setNewLabel} placeholder="Button label" />
        )}
        <Input value={newUrl} onChange={setNewUrl} placeholder="URL" />
        {newType === "locked" && (
          <Input value={newPassword} onChange={setNewPassword} placeholder="Password" />
        )}
        <ActionBtn onClick={addCustom}>+ Add Button</ActionBtn>
      </div>

      <ActionBtn onClick={save} disabled={saving}>
        {saving ? "Saving..." : "Save Page Config"}
      </ActionBtn>
      <StatusMsg status={status} noun="Page config saved" />
    </div>
  );
}


/* ══════════════════════════════════════════════
   ANALYTICS PANEL
══════════════════════════════════════════════ */
function AnalyticsPanel({ releases }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("page_events")
        .select("page_slug,event_type,label,created_at")
        .eq("page_type", "release")
        .order("created_at", { ascending: false });
      setRows(data || []);
      setLoading(false);
    })();
  }, []);

  /* aggregate per release */
  const byRelease = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if (!map[r.page_slug]) map[r.page_slug] = { views: 0, clicks: {}, total_clicks: 0 };
      if (r.event_type === "view") map[r.page_slug].views++;
      if (r.event_type === "click") {
        const k = r.label || "Unknown";
        map[r.page_slug].clicks[k] = (map[r.page_slug].clicks[k] || 0) + 1;
        map[r.page_slug].total_clicks++;
      }
    });
    return map;
  }, [rows]);

  /* sorted by views desc */
  const sorted = useMemo(() =>
    Object.entries(byRelease)
      .sort((a, b) => b[1].views - a[1].views)
      .map(([slug, data]) => {
        const release = releases.find(r => r.slug === slug);
        return { slug, title: release?.title || slug, artist: release?.artist || "", ...data };
      }),
    [byRelease, releases]
  );

  function exportPDF(entry) {
    setPdfLoading(true);
    const dateStr = new Date().toLocaleDateString("he-IL");
    const clickRows = Object.entries(entry.clicks).sort((a,b) => b[1]-a[1]);
    const totalClicks = entry.total_clicks;

    const html = `<!DOCTYPE html><html lang="he" dir="rtl">
<head><meta charset="UTF-8">
<style>
  @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;700;900&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Heebo',Arial,sans-serif;direction:rtl;background:#fff;color:#111;padding:40px}
  .doc-header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:2px solid #111;margin-bottom:28px}
  .brand{font-size:11px;font-weight:700;letter-spacing:0.4em;text-transform:uppercase;color:#111;margin-bottom:4px}
  .doc-type{font-size:9px;letter-spacing:0.25em;text-transform:uppercase;color:#888;margin-bottom:12px}
  .release-name{font-size:26px;font-weight:900;letter-spacing:-0.5px;line-height:1}
  .artist-name{font-size:12px;color:#666;margin-top:4px;letter-spacing:0.05em}
  .kpi-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:28px}
  .kpi{border:1px solid #ddd;padding:18px;text-align:center}
  .kpi-label{font-size:8px;letter-spacing:0.3em;text-transform:uppercase;color:#888;margin-bottom:8px}
  .kpi-val{font-size:28px;font-weight:900;color:#111;font-family:'Courier New',monospace}
  .section-head{font-size:8px;letter-spacing:0.3em;text-transform:uppercase;color:#888;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid #e8e8e8}
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{padding:8px 10px;text-align:right;font-size:8px;letter-spacing:0.2em;text-transform:uppercase;color:#888;border-bottom:1.5px solid #111;font-weight:600}
  th.num{text-align:left}
  td{padding:8px 10px;border-bottom:1px solid #f0f0f0}
  td.num{text-align:left;font-family:'Courier New',monospace;font-weight:700}
  .bar-wrap{flex:1;height:4px;background:#f0f0f0;margin:0 10px;position:relative}
  .bar-fill{position:absolute;right:0;top:0;bottom:0;background:#111}
  tr:nth-child(even) td{background:#fafafa}
  .footer{margin-top:28px;padding-top:14px;border-top:1px solid #ddd;display:flex;justify-content:space-between}
  .footer-brand{font-size:9px;letter-spacing:0.3em;text-transform:uppercase;color:#bbb;font-weight:700}
  .footer-note{font-size:8px;color:#ccc}
  @media print{body{padding:20px}@page{margin:10mm;size:A4}}
</style></head><body>
  <div class="doc-header">
    <div>
      <div class="brand">YEN SOUND</div>
      <div class="doc-type">Analytics Report</div>
      <div class="release-name">${entry.title}</div>
      ${entry.artist ? `<div class="artist-name">${entry.artist}</div>` : ''}
    </div>
    <div style="text-align:left">
      <div style="font-size:10px;color:#888">${dateStr}</div>
      <div style="font-size:10px;color:#555;margin-top:4px">yensound.com/release/${entry.slug}</div>
    </div>
  </div>

  <div class="kpi-grid">
    <div class="kpi"><div class="kpi-label">Page Views</div><div class="kpi-val">${entry.views.toLocaleString()}</div></div>
    <div class="kpi"><div class="kpi-label">Link Clicks</div><div class="kpi-val">${totalClicks.toLocaleString()}</div></div>
  </div>

  ${clickRows.length > 0 ? `
  <div style="margin-bottom:28px">
    <div class="section-head">Clicks by Button</div>
    <table>
      <thead><tr>
        <th>Button</th>
        <th class="num">Clicks</th>
        <th class="num">%</th>
      </tr></thead>
      <tbody>
        ${clickRows.map(([label, count]) => `
          <tr>
            <td>${label}</td>
            <td class="num">${count}</td>
            <td class="num">${totalClicks > 0 ? Math.round(count/totalClicks*100) : 0}%</td>
          </tr>`).join('')}
      </tbody>
    </table>
  </div>` : ''}

  <div class="footer">
    <span class="footer-brand">YEN SOUND</span>
    <span class="footer-note">הופק ${dateStr} · מסמך זה מיועד לאמן בלבד</span>
  </div>
</body></html>`;

    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); setPdfLoading(false); }, 600);
  }

  if (loading) return <p style={{ fontFamily: F, fontSize: "10px", opacity: 0.25 }}>Loading...</p>;
  if (rows.length === 0) return (
    <div>
      <p style={{ fontFamily: F, fontSize: "10px", opacity: 0.25, marginBottom: "12px" }}>No data yet.</p>
      <p style={{ fontFamily: F, fontSize: "9px", opacity: 0.2, lineHeight: 1.7, letterSpacing: "0.08em" }}>
        Analytics are tracked automatically when visitors open release pages and click buttons.
        Make sure to run the SQL below in Supabase first.
      </p>
      <pre style={{ background: "#080808", border: "1px solid #1a1a1a", padding: "16px", fontFamily: "monospace", fontSize: "11px", color: "rgba(100,255,180,0.85)", overflowX: "auto", lineHeight: 1.6, marginTop: "16px", whiteSpace: "pre-wrap" }}>
{`CREATE TABLE IF NOT EXISTS page_events (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  page_type text NOT NULL,
  page_slug text NOT NULL,
  event_type text NOT NULL,
  label text,
  created_at timestamptz DEFAULT now()
);
ALTER TABLE page_events ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Anon insert" ON page_events
  FOR INSERT WITH CHECK (true);
CREATE POLICY "Anon select" ON page_events
  FOR SELECT USING (true);`}
      </pre>
    </div>
  );

  return (
    <div>
      <FieldLabel>Release Performance</FieldLabel>
      {selectedSlug && (() => {
        const entry = sorted.find(s => s.slug === selectedSlug);
        if (!entry) return null;
        const clickRows = Object.entries(entry.clicks).sort((a,b) => b[1]-a[1]);
        const maxClicks = clickRows[0]?.[1] || 1;
        return (
          <div style={{ border: "1px solid rgba(240,237,232,0.15)", marginBottom: "16px", padding: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <p style={{ fontFamily: F, fontSize: "13px", fontWeight: 700, letterSpacing: "0.1em", color: "#f0ede8" }}>{entry.title}</p>
                {entry.artist && <p style={{ fontFamily: F, fontSize: "10px", opacity: 0.4, marginTop: "2px" }}>{entry.artist}</p>}
              </div>
              <GhostBtn onClick={() => setSelectedSlug(null)}>✕</GhostBtn>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px" }}>
              {[["Page Views", entry.views], ["Link Clicks", entry.total_clicks]].map(([label, val]) => (
                <div key={label} style={{ border: "1px solid rgba(240,237,232,0.1)", padding: "14px", textAlign: "center" }}>
                  <p style={{ fontFamily: F, fontSize: "8px", letterSpacing: "0.25em", textTransform: "uppercase", opacity: 0.35, marginBottom: "8px" }}>{label}</p>
                  <p style={{ fontFamily: "monospace", fontSize: "22px", color: "#f0ede8" }}>{val.toLocaleString()}</p>
                </div>
              ))}
            </div>

            {clickRows.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <p style={{ fontFamily: F, fontSize: "8px", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.3, marginBottom: "12px" }}>Clicks by Button</p>
                {clickRows.map(([label, count]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", direction: "rtl" }}>
                    <span style={{ fontFamily: F, fontSize: "10px", width: "100px", textAlign: "right", flexShrink: 0, opacity: 0.7 }}>{label}</span>
                    <div style={{ flex: 1, height: "3px", background: "rgba(240,237,232,0.07)" }}>
                      <div style={{ height: "100%", width: `${(count/maxClicks)*100}%`, background: "rgba(240,237,232,0.5)", transition: "width 0.4s" }} />
                    </div>
                    <span style={{ fontFamily: "monospace", fontSize: "10px", opacity: 0.5, width: "30px", textAlign: "left", flexShrink: 0 }}>{count}</span>
                  </div>
                ))}
              </div>
            )}

            <ActionBtn onClick={() => exportPDF(entry)} disabled={pdfLoading}>
              {pdfLoading ? "Generating..." : "Export PDF Report"}
            </ActionBtn>
          </div>
        );
      })()}

      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {sorted.map(entry => (
          <div key={entry.slug}
            onClick={() => setSelectedSlug(entry.slug === selectedSlug ? null : entry.slug)}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", border: `1px solid ${entry.slug === selectedSlug ? "rgba(240,237,232,0.4)" : "rgba(240,237,232,0.08)"}`, cursor: "pointer", background: entry.slug === selectedSlug ? "#0d0d0d" : "transparent", transition: "all 0.15s" }}
            onMouseOver={e => { if (entry.slug !== selectedSlug) e.currentTarget.style.borderColor = "rgba(240,237,232,0.2)"; }}
            onMouseOut={e => { if (entry.slug !== selectedSlug) e.currentTarget.style.borderColor = "rgba(240,237,232,0.08)"; }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ fontFamily: F, fontSize: "11px", letterSpacing: "0.05em", color: "#f0ede8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{entry.title}</p>
              {entry.artist && <p style={{ fontFamily: F, fontSize: "9px", opacity: 0.35, marginTop: "2px" }}>{entry.artist}</p>}
            </div>
            <div style={{ display: "flex", gap: "16px", flexShrink: 0, marginRight: "8px" }}>
              <div style={{ textAlign: "center" }}>
                <p style={{ fontFamily: "monospace", fontSize: "14px", color: "#f0ede8" }}>{entry.views.toLocaleString()}</p>
                <p style={{ fontFamily: F, fontSize: "7px", opacity: 0.3, letterSpacing: "0.15em", textTransform: "uppercase" }}>views</p>
              </div>
              <div style={{ textAlign: "center" }}>
                <p style={{ fontFamily: "monospace", fontSize: "14px", color: "#f0ede8" }}>{entry.total_clicks.toLocaleString()}</p>
                <p style={{ fontFamily: F, fontSize: "7px", opacity: 0.3, letterSpacing: "0.15em", textTransform: "uppercase" }}>clicks</p>
              </div>
            </div>
            <span style={{ opacity: 0.3, fontSize: "10px" }}>{entry.slug === selectedSlug ? "▲" : "▼"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════
   ACTIVITY LOG PANEL  (#2)
══════════════════════════════════════════════ */
function ActivityLogPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("artist_activity")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(80);
      setLogs(data || []);
      setLoading(false);
    })();
  }, []);

  function timeAgo(ts) {
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (m < 2) return "just now";
    if (m < 60) return m + "m ago";
    if (h < 24) return h + "h ago";
    if (d < 7) return d + "d ago";
    return new Date(ts).toLocaleDateString("he-IL");
  }

  const ACTION_LABELS = {
    bio_update:     { label: "Updated bio",        icon: "✎" },
    photo_update:   { label: "Changed photo",      icon: "◻" },
    theme_change:   { label: "Changed theme",      icon: "◑" },
    buttons_update: { label: "Updated links",      icon: "⊞" },
    order_update:   { label: "Reordered buttons",  icon: "=" },
    merch_toggle:   { label: "Toggled merch",      icon: "◑" },
    reset:          { label: "Reset page",         icon: "↺" },
  };

  if (loading) return <p style={{ fontFamily: F, fontSize: "10px", opacity: 0.25 }}>Loading...</p>;

  if (logs.length === 0) return (
    <div>
      <p style={{ fontFamily: F, fontSize: "10px", opacity: 0.25, marginBottom: "16px" }}>No activity yet.</p>
      <p style={{ fontFamily: F, fontSize: "9px", opacity: 0.2, lineHeight: 1.7, letterSpacing: "0.06em", marginBottom: "16px" }}>
        Activity is logged automatically when artists make changes in their dashboard.
        Run this SQL in Supabase first:
      </p>
      <pre style={{ background: "#080808", border: "1px solid #1a1a1a", padding: "16px", fontFamily: "monospace", fontSize: "11px", color: "rgba(100,255,180,0.85)", overflowX: "auto", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
{`CREATE TABLE IF NOT EXISTS artist_activity (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  artist_slug text NOT NULL,
  artist_name text,
  action text NOT NULL,
  detail text,
  created_at timestamptz DEFAULT now()
);
ALTER TABLE artist_activity ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Anon insert" ON artist_activity
  FOR INSERT WITH CHECK (true);
CREATE POLICY "Anon select" ON artist_activity
  FOR SELECT USING (true);`}
      </pre>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      {logs.map(log => {
        const meta = ACTION_LABELS[log.action] || { label: log.action, icon: "·" };
        return (
          <div key={log.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px", border: "1px solid rgba(240,237,232,0.06)", background: "transparent" }}>
            <span style={{ fontFamily: F, fontSize: "12px", opacity: 0.35, flexShrink: 0, width: "16px", textAlign: "center" }}>{meta.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: F, fontSize: "11px", color: D.text }}>
                <span style={{ fontWeight: 700, opacity: 0.9 }}>{log.artist_name || log.artist_slug}</span>
                <span style={{ opacity: 0.45 }}> · {meta.label}</span>
                {log.detail && <span style={{ opacity: 0.3, fontSize: "10px" }}> ({log.detail})</span>}
              </p>
            </div>
            <span style={{ fontFamily: "monospace", fontSize: "9px", opacity: 0.25, flexShrink: 0 }}>{timeAgo(log.created_at)}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════
   SUBMISSIONS PANEL
══════════════════════════════════════════════ */
const SHEET_ID = "1lfrIsCWGblDhu1NdZEJ5BUhNpBqmKHkwKRp7sNYnIfo";
const SHEET_NAME = "Form Responses 1";

function formatDateEU(val) {
  if (!val) return "?";
  try {
    var d = new Date(val);
    if (isNaN(d.getTime())) return val;
    var day   = String(d.getDate()).padStart(2, "0");
    var month = String(d.getMonth() + 1).padStart(2, "0");
    var year  = d.getFullYear();
    return day + "/" + month + "/" + year;
  } catch(e) { return val; }
}

function buildDspEmail(sub) {
  var lines = [];
  var typeHe = sub.type === "Single" ? "סינגל" : sub.type === "Album" ? "אלבום" : sub.type === "EP" ? "EP" : (sub.type || "?");
  lines.push("\u05e9\u05dc\u05d5\u05dd \u05e8\u05d1, \u05e0\u05e9\u05de\u05d7 \u05dc\u05e9\u05dc\u05d5\u05d7 \u05e8\u05dc\u05d9\u05e1 \u05d7\u05d3\u05e9 \u05dc\u05d4\u05e4\u05e6\u05d4 \u05e2\u05f4\u05e4 \u05d4\u05e4\u05d9\u05e8\u05d5\u05d8 \u05d4\u05e0\u05dc");
  lines.push("");
  lines.push("\u05e9\u05dd \u05d0\u05de\u05df: " + (sub.artist || "?"));
  if (sub.features) lines.push("\u05d0\u05de\u05e0\u05d9\u05dd \u05d0\u05d5\u05e8\u05d7\u05d9\u05dd: " + sub.features);
  lines.push("\u05e9\u05dd \u05e8\u05dc\u05d9\u05e1: " + (sub.title || "?"));
  lines.push("\u05e1\u05d5\u05d2 \u05e8\u05dc\u05d9\u05e1: " + typeHe + (sub.trackCount ? " (" + sub.trackCount + " \u05e9\u05d9\u05e8\u05d9\u05dd)" : ""));
  lines.push("\u05ea\u05d0\u05e8\u05d9\u05da \u05e9\u05d7\u05e8\u05d5\u05e8: " + formatDateEU(sub.releaseDate));
  lines.push("");
  lines.push("\u05e7\u05d1\u05e6\u05d9\u05dd");
  if (sub.artwork)        lines.push("\u05d0\u05e8\u05d8: " + sub.artwork);
  if (sub.master)         lines.push("\u05de\u05d0\u05e1\u05d8\u05e8: " + sub.master);
  if (sub.lyrics)         lines.push("\u05e7\u05d5\u05d1\u05e5 \u05de\u05d9\u05dc\u05d9\u05dd \u05d5\u05e7\u05e8\u05d3\u05d9\u05d8\u05d9\u05dd: " + sub.lyrics);
  if (sub.tracklist)      lines.push("\u05d8\u05e8\u05e7\u05dc\u05d9\u05e1\u05d8: " + sub.tracklist);
  if (sub.canvas)         lines.push("Spotify Canvas: " + sub.canvas);
  if (sub.musicVideoFile) lines.push("\u05e7\u05dc\u05d9\u05e4: " + sub.musicVideoFile);
  lines.push("");
  lines.push("\u05e9\u05d5\u05e0\u05d5\u05ea");
  var misc = [];
  if (sub.tiktokIG)      misc.push("\u05e1\u05d0\u05d5\u05e0\u05d3\u05d9\u05dd \u05d8\u05d9\u05e7\u05d8\u05d5\u05e7 \u05d5\u05d0\u05d9\u05e0\u05e1\u05d8\u05d2\u05e8\u05dd: " + sub.tiktokIG);
  if (sub.tiktokTiming)  misc.push("\u05ea\u05d6\u05de\u05d5\u05df \u05d8\u05d9\u05e7\u05d8\u05d5\u05e7: " + sub.tiktokTiming);
  if (sub.notes)         misc.push("\u05d4\u05e2\u05e8\u05d5\u05ea: " + sub.notes);
  if (misc.length) lines.push(misc.join("\n"));
  else lines.push("-");
  return lines.join("\n");
}

function buildDraftSubject(sub) {
  var typeHe = sub.type === "Single" ? "סינגל" : sub.type === "Album" ? "אלבום" : sub.type === "EP" ? "EP" : (sub.type || "");
  return "רליס מוכן: " + (sub.artist || "?") + (sub.title ? " - " + sub.title : "") + (typeHe ? " (" + typeHe + ")" : "");
}

/* Best-effort "YYYY-MM-DD" normalization for whatever date format the
   submission sheet hands back. Returns "" if it can't be parsed. */
function normalizeToISODate(val) {
  if (!val) return "";
  var d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/* Rolls up everything from a submission that doesn't map to a structured
   release field into one readable notes block, so nothing gets lost when
   a submission is turned into a release. */
function buildInternalNotesFromSubmission(sub) {
  var lines = ["Imported from submission" + (sub.timestamp ? " (" + sub.timestamp + ")" : "")];
  if (sub.trackCount)      lines.push("Track count: " + sub.trackCount);
  if (sub.features)        lines.push("Features: " + sub.features);
  if (sub.master)          lines.push("Master: " + sub.master);
  if (sub.lyrics)          lines.push("Lyrics/credits: " + sub.lyrics);
  if (sub.tracklist)       lines.push("Tracklist: " + sub.tracklist);
  if (sub.canvas)          lines.push("Spotify Canvas: " + sub.canvas);
  if (sub.musicVideoFile)  lines.push("Music video file: " + sub.musicVideoFile);
  if (sub.tiktokIG)        lines.push("TikTok/IG handles: " + sub.tiktokIG);
  if (sub.tiktokTiming)    lines.push("TikTok timing: " + sub.tiktokTiming);
  if (sub.pitchTrack)      lines.push("Pitch track: " + sub.pitchTrack);
  if (sub.description)     lines.push("About: " + sub.description);
  if (sub.notes)           lines.push("Notes: " + sub.notes);
  return lines.join("\n");
}

/* Checklist items shown per submission. Keys map to sub fields where a
   direct file link exists (artwork/master/lyrics/tracklist); release_date
   and metadata are plain verification checkboxes with no link. Edit this
   array to change what's tracked. */
const CHECKLIST_ITEMS = [
  { key: "artwork",      label: "Artwork" },
  { key: "master",       label: "Master WAV" },
  { key: "lyrics",       label: "Lyrics & Credits" },
  { key: "tracklist",    label: "Tracklist" },
  { key: "release_date", label: "Release Date Confirmed" },
  { key: "metadata",     label: "Metadata Reviewed (artist/title/type)" },
];

function subKey(sub, i) {
  return sub.timestamp || [sub.artist, sub.title, i].join("|");
}

const CHECKLIST_SETUP_SQL = `CREATE TABLE submission_checklist (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  submission_key text UNIQUE NOT NULL,
  checklist jsonb DEFAULT '{}'::jsonb,
  draft_created boolean DEFAULT false,
  gmail_draft_id text,
  updated_at timestamptz DEFAULT now()
);

ALTER TABLE submission_checklist ENABLE ROW LEVEL SECURITY;

CREATE POLICY "Public read" ON submission_checklist
  FOR SELECT USING (true);

CREATE POLICY "Anon write" ON submission_checklist
  FOR ALL USING (true) WITH CHECK (true);`;

function SubmissionsPanel({ onPostRelease }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [copied, setCopied]     = useState(null);
  const [checklists, setChecklists] = useState({}); // submission_key -> row from submission_checklist
  const [checklistTableError, setChecklistTableError] = useState(null);
  const [draftStatus, setDraftStatus] = useState({}); // submission_key -> "sending" | "sent" | "error:<msg>"
  const KEY = process.env.REACT_APP_SHEETS_API_KEY;

  useEffect(() => { fetchSheet(); loadChecklists(); }, []); // eslint-disable-line

  async function loadChecklists() {
    const { data, error } = await supabase.from("submission_checklist").select("*");
    if (error) { setChecklistTableError(error.message); return; }
    setChecklistTableError(null);
    const map = {};
    (data || []).forEach(function(row) { map[row.submission_key] = row; });
    setChecklists(map);
  }

  async function toggleCheck(key, itemKey) {
    const current = checklists[key]?.checklist || {};
    const next = { ...current, [itemKey]: !current[itemKey] };
    setChecklists(function(p) { return { ...p, [key]: { ...(p[key] || {}), submission_key: key, checklist: next } }; });
    const { data, error } = await supabase
      .from("submission_checklist")
      .upsert({ submission_key: key, checklist: next, updated_at: new Date().toISOString() }, { onConflict: "submission_key" })
      .select().single();
    if (!error && data) setChecklists(function(p) { return { ...p, [key]: data }; });
  }

  async function createDraft(sub, key) {
    setDraftStatus(function(p) { return { ...p, [key]: "sending" }; });
    try {
      const res = await fetch("/api/create-gmail-draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject: buildDraftSubject(sub), body: buildDspEmail(sub) }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Failed to create draft");
      setDraftStatus(function(p) { return { ...p, [key]: "sent" }; });
      const { data } = await supabase
        .from("submission_checklist")
        .upsert({ submission_key: key, draft_created: true, gmail_draft_id: json.draftId || null, updated_at: new Date().toISOString() }, { onConflict: "submission_key" })
        .select().single();
      if (data) setChecklists(function(p) { return { ...p, [key]: data }; });
      setTimeout(function() { setDraftStatus(function(p) { return { ...p, [key]: null }; }); }, 4000);
    } catch (e) {
      setDraftStatus(function(p) { return { ...p, [key]: "error:" + e.message }; });
    }
  }

  async function fetchSheet() {
    setLoading(true); setError(null);
    try {
      var range = encodeURIComponent(SHEET_NAME + "!A1:W200");
      var url = "https://sheets.googleapis.com/v4/spreadsheets/" + SHEET_ID + "/values/" + range + "?key=" + KEY;
      var res = await fetch(url);
      var json = await res.json();
      if (json.error) { setError(json.error.message); setLoading(false); return; }
      var vals = json.values || [];
      var data = vals.slice(1).filter(function(r) { return r && r[5]; }).map(function(r) {
        return {
          artist: r[5] || "", title: r[20] || "", type: r[6] || "",
          trackCount: r[7] || "", releaseDate: r[9] || "",
          artwork: r[10] || "", master: r[11] || "", lyrics: r[12] || "",
          tracklist: r[22] || "", canvas: r[18] || "",
          description: r[13] || "", musicVideo: r[14] || "",
          musicVideoFile: r[15] || "", tiktokIG: r[16] || "",
          tiktokTiming: r[17] || "", pitchTrack: r[8] || "",
          features: r[21] || "", notes: r[19] || "",
          sent: r[0] || "", timestamp: r[4] || "",
        };
      }).reverse();
      setRows(data);
    } catch(e) { setError(e.message); }
    setLoading(false);
  }

  async function copyEmail(sub, key) {
    await navigator.clipboard.writeText(buildDspEmail(sub));
    setCopied(key);
    setTimeout(function() { setCopied(null); }, 2000);
  }

  if (loading) return <p style={{ fontFamily: F, fontSize: "11px", color: D.muted }}>Loading...</p>;
  if (error)   return (
    <div>
      <p style={{ fontFamily: F, fontSize: "11px", color: D.red, marginBottom: "10px" }}>{error}</p>
      <GhostBtn onClick={fetchSheet}>Retry</GhostBtn>
    </div>
  );
  if (!rows.length) return <p style={{ fontFamily: F, fontSize: "11px", color: D.muted }}>No submissions yet.</p>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <FieldLabel>{rows.length} Submissions</FieldLabel>
        <GhostBtn onClick={fetchSheet}>Refresh</GhostBtn>
      </div>

      {checklistTableError && (
        <div style={{ marginBottom: "16px", padding: "14px", border: "1px solid rgba(248,113,113,0.3)", borderRadius: D.radiusSm, background: "rgba(248,113,113,0.05)" }}>
          <p style={{ fontFamily: F, fontSize: "10px", color: D.red, marginBottom: "10px" }}>⚠ Checklist table missing: {checklistTableError}</p>
          <p style={{ fontFamily: F, fontSize: "9px", color: D.muted, marginBottom: "8px", letterSpacing: "0.04em" }}>Run this once in Supabase → SQL Editor, then reload:</p>
          <pre style={{ background: "#080808", border: "1px solid #1a1a1a", padding: "14px", fontFamily: "monospace", fontSize: "10px", color: "rgba(100,255,180,0.85)", overflowX: "auto", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {CHECKLIST_SETUP_SQL}
          </pre>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {rows.map(function(sub, i) {
          var isOpen = expanded === i;
          var key = subKey(sub, i);
          var cl = (checklists[key] && checklists[key].checklist) || {};
          var doneCount = CHECKLIST_ITEMS.filter(function(it) { return cl[it.key]; }).length;
          var draftCreated = !!(checklists[key] && checklists[key].draft_created);
          var ds = draftStatus[key];
          return (
            <div key={key} style={{ background: D.surface, border: "1px solid " + (isOpen ? D.borderHi : D.border), borderRadius: D.radius, overflow: "hidden" }}>
              <div onClick={function() { setExpanded(isOpen ? null : i); }}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", cursor: "pointer" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: F, fontSize: "13px", fontWeight: 600, color: D.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {sub.artist}{sub.title ? " - " + sub.title : ""}
                  </p>
                  <p style={{ fontFamily: F, fontSize: "10px", color: D.muted, marginTop: "2px" }}>
                    {sub.type}{sub.trackCount ? " - " + sub.trackCount + " tracks" : ""}
                    {sub.releaseDate ? "  " + sub.releaseDate : ""}
                    {(sub.sent === "TRUE" || sub.sent === true) ? "  (Sent)" : ""}
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, marginLeft: "12px" }}>
                  <span style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.06em", color: doneCount === CHECKLIST_ITEMS.length ? D.green : D.muted }}>
                    {doneCount}/{CHECKLIST_ITEMS.length}
                  </span>
                  {draftCreated && <span style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.06em", color: D.green }}>✓ draft</span>}
                  <span style={{ fontSize: "10px", color: D.muted }}>{isOpen ? "close" : "open"}</span>
                </div>
              </div>
              {isOpen && (
                <div style={{ padding: "0 16px 16px", borderTop: "1px solid " + D.border }}>

                  <FieldLabel>Release Checklist</FieldLabel>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px", marginTop: "10px", marginBottom: "16px" }}>
                    {CHECKLIST_ITEMS.map(function(item) {
                      var link = sub[item.key];
                      return (
                        <label key={item.key} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "7px 10px", cursor: "pointer", borderRadius: D.radiusSm }}
                          onMouseOver={function(e) { e.currentTarget.style.background = D.surface2; }}
                          onMouseOut={function(e) { e.currentTarget.style.background = "transparent"; }}
                        >
                          <input type="checkbox" checked={!!cl[item.key]} onChange={function() { toggleCheck(key, item.key); }}
                            style={{ width: "14px", height: "14px", accentColor: "rgba(74,222,128,0.9)", cursor: "pointer", flexShrink: 0 }} />
                          <span style={{ fontFamily: F, fontSize: "12px", color: cl[item.key] ? D.text : D.muted, flex: 1, textDecoration: cl[item.key] ? "none" : "none" }}>
                            {item.label}
                          </span>
                          {link && (
                            <a href={link} target="_blank" rel="noreferrer" onClick={function(e) { e.stopPropagation(); }}
                              style={{ fontFamily: F, fontSize: "10px", color: D.muted, textDecoration: "none", border: "1px solid " + D.border, borderRadius: D.radiusSm, padding: "3px 8px" }}>
                              open
                            </a>
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {(sub.musicVideoFile || sub.canvas) && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "14px" }}>
                      {[["Music Video", sub.musicVideoFile], ["Canvas", sub.canvas]].filter(function(x) { return x[1]; }).map(function(item) {
                        return (
                          <a key={item[0]} href={item[1]} target="_blank" rel="noreferrer"
                            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: D.surface2, border: "1px solid " + D.border, borderRadius: D.radiusSm, textDecoration: "none" }}>
                            <span style={{ fontFamily: F, fontSize: "11px", fontWeight: 600, color: D.text }}>{item[0]}</span>
                            <span style={{ fontSize: "10px", color: D.muted }}>open</span>
                          </a>
                        );
                      })}
                    </div>
                  )}

                  {(sub.features || sub.pitchTrack || sub.tiktokIG || sub.tiktokTiming || sub.description || sub.notes) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "14px" }}>
                      {sub.features     && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted }}>Feat: {sub.features}</p>}
                      {sub.pitchTrack   && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted }}>Pitch: {sub.pitchTrack}</p>}
                      {sub.tiktokIG     && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted }}>TikTok/IG: {sub.tiktokIG}</p>}
                      {sub.tiktokTiming && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted }}>TikTok timing: {sub.tiktokTiming}</p>}
                      {sub.description  && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted }}>About: {sub.description}</p>}
                      {sub.notes        && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted }}>Notes: {sub.notes}</p>}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: "8px" }}>
                    <ActionBtn onClick={function() { copyEmail(sub, key); }}>
                      {copied === key ? "Copied!" : "Copy DSP Email"}
                    </ActionBtn>
                    <ActionBtn onClick={function() { createDraft(sub, key); }} disabled={ds === "sending"}>
                      {ds === "sending" ? "Creating..." : draftCreated ? "Draft Created ✓ — Recreate" : "Create Gmail Draft"}
                    </ActionBtn>
                  </div>
                  {onPostRelease && (
                    <div style={{ marginTop: "8px" }}>
                      <ActionBtn onClick={function() { onPostRelease(sub); }}>
                        Post Release on Site →
                      </ActionBtn>
                    </div>
                  )}
                  {doneCount < CHECKLIST_ITEMS.length && (
                    <p style={{ fontFamily: F, fontSize: "9px", color: D.muted, marginTop: "8px", letterSpacing: "0.04em" }}>
                      {CHECKLIST_ITEMS.length - doneCount} checklist item{CHECKLIST_ITEMS.length - doneCount === 1 ? "" : "s"} still unchecked
                    </p>
                  )}
                  {ds === "sent" && <StatusMsg status="success" noun="Draft created — check info@sigh.live Drafts" />}
                  {ds && ds.indexOf("error:") === 0 && <StatusMsg status={ds} noun="" />}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════
   MERCH PANEL
══════════════════════════════════════════════ */
/* ══════════════════════════════════════════════
   SIGNED FORMS (distribution forms signed in the artist dashboard)
   Data lives in a private bucket/table, reached only via /api/signed-forms.
   Unlocking needs a fresh authenticator code → 12h token in sessionStorage.
══════════════════════════════════════════════ */
const FORMS_TOKEN_KEY = "yenFormsToken";
const LABEL_SIG_KEY = "yenLabelSignature";

function getFormsToken() {
  try {
    const t = JSON.parse(sessionStorage.getItem(FORMS_TOKEN_KEY) || "null");
    return t && t.exp > Date.now() ? t.token : null;
  } catch { return null; }
}

async function formsApi(action, payload = {}, token) {
  const res = await fetch("/api/signed-forms", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { "x-admin-token": token } : {}) },
    body: JSON.stringify({ action, ...payload }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(json.message || "Request failed"), json);
  return json;
}

const FORM_STATUS = {
  pending:  { label: "Awaiting your signature", color: "rgba(250,204,21,0.9)",  bg: "rgba(250,204,21,0.08)" },
  approved: { label: "Signed & approved",       color: "rgba(74,222,128,0.9)",  bg: "rgba(74,222,128,0.08)" },
  rejected: { label: "Rejected",                color: "rgba(248,113,113,0.9)", bg: "rgba(248,113,113,0.08)" },
};

/* ── PIL (Tepez) hand-off ──────────────────────────────────────────── */
function pilFileName(form) {
  const who = (form.signer_name || form.artist_name || "artist").replace(/[\\/:*?"<>|]/g, "");
  return `YEN_הצהרה_${who}_PIL.pdf`;
}

async function downloadUrlAs(url, filename) {
  const blob = await fetch(url).then(r => { if (!r.ok) throw new Error("download failed"); return r.blob(); });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 4000);
}

function PilSetupCard({ onClose }) {
  const [d, setD] = useState(() => loadDeclarer() || { first: "", last: "", id: "", phone: "", email: "" });
  const [saved, setSaved] = useState(declarerComplete(loadDeclarer()));
  const [copied, setCopied] = useState(false);
  const linkRef = useRef(null);
  const ok = declarerComplete(d);
  const code = ok ? buildBookmarklet(d) : "";

  // React blocks javascript: URLs in href, so the bookmark link gets it set directly.
  useEffect(() => { if (linkRef.current && code) linkRef.current.setAttribute("href", code); }, [code, saved]);

  const field = (key, label, extra = {}) => (
    <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontFamily: F, fontSize: "11px", color: D.muted }}>
      {label}
      <input value={d[key]} onChange={e => { setD({ ...d, [key]: e.target.value }); setSaved(false); }} {...extra}
        style={{ background: D.surface2, border: `1px solid ${D.border}`, color: D.text, fontFamily: F, fontSize: "13px", padding: "8px 10px", borderRadius: D.radiusSm, outline: "none" }} />
    </label>
  );

  return (
    <div style={{ border: `1px solid ${D.borderHi}`, borderRadius: D.radiusSm, padding: "14px", marginBottom: "14px", background: D.surface }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
        <p style={{ fontFamily: F, fontSize: "13px", fontWeight: 700, color: D.text }}>PIL autofill setup</p>
        <GhostBtn onClick={onClose}>Close</GhostBtn>
      </div>
      <p style={{ fontFamily: F, fontSize: "11px", color: D.muted, lineHeight: 1.6, marginBottom: "12px" }}>
        Your details as the declarer on the Tepez form. Saved only in this browser and inside the bookmark, never on the server.
      </p>
      <div dir="rtl" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
        {field("first", "שם פרטי")}
        {field("last", "שם משפחה")}
        {field("id", "מספר זהות", { inputMode: "numeric" })}
        {field("phone", "טלפון נייד", { inputMode: "tel" })}
        {field("email", "כתובת אימייל", { type: "email" })}
      </div>
      <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "12px", flexWrap: "wrap" }}>
        <GhostBtn onClick={() => { if (ok) { saveDeclarer(d); setSaved(true); } }}>{saved ? "Saved ✓" : "Save details"}</GhostBtn>
        {ok && saved && (
          <>
            <a ref={linkRef} href="#yen-pil" draggable="true" onClick={e => e.preventDefault()}
              style={{ fontFamily: F, fontSize: "12px", fontWeight: 700, padding: "7px 14px", borderRadius: "999px", background: D.text, color: "#000", textDecoration: "none", cursor: "grab" }}>
              YEN → PIL
            </a>
            <GhostBtn onClick={async () => { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
              {copied ? "Copied ✓" : "Copy bookmark code"}
            </GhostBtn>
          </>
        )}
      </div>
      {ok && saved && (
        <p style={{ fontFamily: F, fontSize: "11px", color: D.muted, lineHeight: 1.6, marginTop: "10px" }}>
          Drag the white "YEN → PIL" pill to your bookmarks bar (once; again if you change your details). Safari: add any bookmark, edit it, and paste the copied code as its address.
        </p>
      )}
    </div>
  );
}

function CountersignModal({ form, token, onClose, onDone }) {
  const padRef = useRef(null);
  const [empty, setEmpty] = useState(true);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const saved = (() => { try { return localStorage.getItem(LABEL_SIG_KEY); } catch { return null; } })();

  async function confirm() {
    if (empty) { setErr("Sign first"); return; }
    setBusy(true); setErr("");
    try {
      const sigBytes = padRef.current.toPngBytes();
      if (remember) { try { localStorage.setItem(LABEL_SIG_KEY, padRef.current.toDataUrl()); } catch (_) {} }
      const original = await fetch(form.url).then(r => { if (!r.ok) throw new Error("Couldn't load the signed PDF"); return r.arrayBuffer(); });
      const out = await countersignForm(original, sigBytes);
      await formsApi("countersign", { id: form.id, pdfBase64: bytesToBase64(out) }, token);
      // The full 3-page copy stays in storage (View full, internal). What lands on your computer is the PIL copy.
      downloadPdf(await pilCopy(out), pilFileName(form));
      onDone();
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: "460px", background: D.surface, border: `1px solid ${D.borderHi}`, borderRadius: D.radius, padding: "20px", boxShadow: D.shadowLg }}>
        <p style={{ fontFamily: F, fontSize: "14px", fontWeight: 700, color: D.text }}>Countersign for YEN SOUND</p>
        <p style={{ fontFamily: F, fontSize: "11px", color: D.muted, margin: "4px 0 14px", lineHeight: 1.5 }}>
          {form.artist_name} · {form.signer_name}. Your signature and today's date go on נספח ב׳ ("חתימה בשם YEN SOUND").
        </p>
        <SignaturePad ref={padRef} onChange={setEmpty} height={170} placeholder="Sign here" />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px", gap: "8px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: "6px" }}>
            {saved && <GhostBtn onClick={() => { padRef.current.clear(); padRef.current.loadDataUrl(saved); }}>Use saved signature</GhostBtn>}
            <GhostBtn onClick={() => padRef.current.clear()}>Clear</GhostBtn>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontFamily: F, fontSize: "11px", color: D.muted, cursor: "pointer" }}>
            <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} /> Remember on this device
          </label>
        </div>
        {err && <p style={{ fontFamily: F, fontSize: "12px", color: D.red, marginTop: "10px" }}>{err}</p>}
        <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
          <div style={{ flex: 1 }}><ActionBtn onClick={onClose} disabled={busy}>Cancel</ActionBtn></div>
          <div style={{ flex: 2 }}><ActionBtn onClick={confirm} disabled={busy}>{busy ? "Signing…" : "Sign & approve"}</ActionBtn></div>
        </div>
      </div>
    </div>
  );
}

function SignedFormsPanel() {
  const [token, setToken] = useState(getFormsToken);
  const [code, setCode] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [missingSetup, setMissingSetup] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [openId, setOpenId] = useState(null);
  const [signing, setSigning] = useState(null);
  const [pilSetup, setPilSetup] = useState(false);
  const [pilBusy, setPilBusy] = useState(null);

  const relock = useCallback(() => { sessionStorage.removeItem(FORMS_TOKEN_KEY); setToken(null); }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const { rows } = await formsApi("list", {}, token);
      setRows(rows || []); setMissingSetup(false);
    } catch (e) {
      if (e.relock) relock();
      else if (e.missingSetup) setMissingSetup(true);
      else setError(e.message);
    }
    setLoading(false);
  }, [token, relock]);

  useEffect(() => { load(); }, [load]);

  async function unlock() {
    if (!code.trim()) return;
    setUnlocking(true); setError("");
    try {
      const { token: t, expiresIn } = await formsApi("unlock", { code: code.trim() });
      sessionStorage.setItem(FORMS_TOKEN_KEY, JSON.stringify({ token: t, exp: Date.now() + expiresIn - 60000 }));
      setToken(t); setCode("");
    } catch (e) { setError(e.message); }
    setUnlocking(false);
  }

  async function setStatus(form, action) {
    let note;
    if (action === "reject") {
      note = window.prompt("Reason (optional, internal):", "");
      if (note === null) return;
    }
    try { await formsApi(action, { id: form.id, note }, token); load(); }
    catch (e) { if (e.relock) relock(); else setError(e.message); }
  }

  async function exportPil(f) {
    setPilBusy(f.id); setError("");
    try {
      const full = await fetch(f.url).then(r => { if (!r.ok) throw new Error("Couldn't load the PDF"); return r.arrayBuffer(); });
      downloadPdf(await pilCopy(full), pilFileName(f));
    } catch (e) { setError(e.message); }
    setPilBusy(null);
  }

  async function sendToPil(f) {
    if (!declarerComplete(loadDeclarer())) { setPilSetup(true); return; }
    const payload = buildPilPayload(f);
    // Open the tab inside the click, before any await, so popup blockers allow it.
    const win = window.open("about:blank", "_blank");
    setPilBusy(f.id); setError("");
    try {
      try { await navigator.clipboard.writeText(encodePayload(payload)); } catch (_) {}
      if (win) win.location.href = tepezLink(payload);
      const full = await fetch(f.url).then(r => { if (!r.ok) throw new Error("Couldn't load the PDF"); return r.arrayBuffer(); });
      downloadPdf(await pilCopy(full), pilFileName(f));
      // Covers for "עטיפת הסינגל", from releases already on the site.
      const titles = [...new Set((f.tracks || []).flatMap(t => [t.release, t.track]).filter(Boolean).map(t => t.trim()))];
      if (titles.length) {
        const { data } = await supabase.from("releases").select("title,cover").in("title", titles);
        for (const r of (data || []).filter(r => r.cover)) {
          try { await downloadUrlAs(r.cover, `cover_${r.title.replace(/[\\/:*?"<>|]/g, "")}.jpg`); } catch (_) {}
        }
      }
      await formsApi("mark_pil", { id: f.id }, token).catch(() => {});
      load();
    } catch (e) { setError(e.message); }
    setPilBusy(null);
  }

  if (!token) {
    return (
      <div>
        <p style={{ fontFamily: F, fontSize: "15px", fontWeight: 700, color: D.text, marginBottom: "6px" }}>Signed Forms</p>
        <p style={{ fontFamily: F, fontSize: "12px", color: D.muted, lineHeight: 1.6, marginBottom: "16px" }}>
          These contain artists' ID numbers, so they need a fresh authenticator code. Stays unlocked for 12 hours in this tab.
        </p>
        <div style={{ display: "flex", gap: "8px" }}>
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code"
            onKeyDown={e => e.key === "Enter" && unlock()} aria-label="Authenticator code"
            style={{ flex: 1, background: D.surface2, border: `1px solid ${D.border}`, color: D.text, fontFamily: F, fontSize: "15px", letterSpacing: "0.2em", padding: "10px 14px", borderRadius: D.radiusSm, outline: "none" }} />
          <div style={{ width: "120px" }}><ActionBtn onClick={unlock} disabled={unlocking}>{unlocking ? "…" : "Unlock"}</ActionBtn></div>
        </div>
        {error && <p style={{ fontFamily: F, fontSize: "12px", color: D.red, marginTop: "10px" }}>{error}</p>}
      </div>
    );
  }

  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const shown = filter === "all" ? rows : rows.filter(r => r.status === filter);
  const fmt = ts => new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
        <p style={{ fontFamily: F, fontSize: "15px", fontWeight: 700, color: D.text }}>Signed Forms</p>
        <div style={{ display: "flex", gap: "6px" }}>
          <GhostBtn onClick={() => setPilSetup(v => !v)}>PIL setup</GhostBtn>
          <GhostBtn onClick={load}>{loading ? "Loading…" : "Refresh"}</GhostBtn>
          <GhostBtn onClick={relock}>Lock</GhostBtn>
        </div>
      </div>

      {pilSetup && <PilSetupCard onClose={() => setPilSetup(false)} />}

      {missingSetup && (
        <div style={{ padding: "12px 14px", border: "1px solid rgba(250,204,21,0.3)", background: "rgba(250,204,21,0.06)", borderRadius: D.radiusSm, marginBottom: "12px" }}>
          <p style={{ fontFamily: F, fontSize: "12px", color: D.text, lineHeight: 1.6 }}>
            Setup needed: run <code>SIGNED_FORMS_SETUP.sql</code> in the Supabase SQL editor, and make sure <code>SUPABASE_SERVICE_ROLE_KEY</code> is set in Vercel.
          </p>
        </div>
      )}
      {error && <StatusMsg status={`error:${error}`} />}

      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "12px" }}>
        {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]].map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)} style={{
            fontFamily: F, fontSize: "11px", fontWeight: 600, padding: "6px 12px", borderRadius: "999px", cursor: "pointer",
            border: `1px solid ${filter === id ? "rgba(74,222,128,0.4)" : D.border}`,
            background: filter === id ? "rgba(74,222,128,0.08)" : "transparent", color: filter === id ? D.text : D.muted,
          }}>{label}{id !== "all" && counts[id] ? ` · ${counts[id]}` : ""}</button>
        ))}
      </div>

      {!loading && !shown.length && !missingSetup && (
        <p style={{ fontFamily: F, fontSize: "12px", color: D.muted, padding: "18px 0", textAlign: "center" }}>Nothing here.</p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {shown.map(f => {
          const st = FORM_STATUS[f.status] || FORM_STATUS.pending;
          const open = openId === f.id;
          const tracks = Array.isArray(f.tracks) ? f.tracks : [];
          return (
            <div key={f.id} style={{ background: D.surface, border: `1px solid ${open ? D.borderHi : D.border}`, borderRadius: D.radiusSm }}>
              <button onClick={() => setOpenId(open ? null : f.id)} style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: "12px 14px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", color: D.text }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontFamily: F, fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {f.artist_name} <span style={{ color: D.muted, fontWeight: 400 }}>· {f.signer_name}</span>
                  </p>
                  <p style={{ fontFamily: F, fontSize: "11px", color: D.muted, marginTop: "2px" }}>
                    {fmt(f.created_at)} · {tracks.length} track{tracks.length === 1 ? "" : "s"}{f.id_last4 ? ` · ID ••${f.id_last4}` : ""}
                  </p>
                </div>
                <span style={{ flexShrink: 0, fontFamily: F, fontSize: "10px", fontWeight: 600, padding: "4px 8px", borderRadius: "999px", color: st.color, background: st.bg }}>{st.label}</span>
              </button>

              {open && (
                <div style={{ padding: "0 14px 14px", borderTop: `1px solid ${D.border}` }}>
                  <div dir="rtl" style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {tracks.map((t, i) => (
                      <p key={i} style={{ fontFamily: F, fontSize: "12px", color: D.text, textAlign: "right" }}>
                        {i + 1}. {t.track} <span style={{ color: D.muted }}>· {t.storePerformer || f.artist_name}{t.release ? ` · ${t.release}` : ""}</span>
                      </p>
                    ))}
                    {f.notes && <p style={{ fontFamily: F, fontSize: "12px", color: D.muted, textAlign: "right", marginTop: "6px" }}>הערות: {f.notes}</p>}
                  </div>
                  {f.review_note && <p style={{ fontFamily: F, fontSize: "11px", color: D.red, marginTop: "8px" }}>Note: {f.review_note}</p>}
                  {f.countersigned_at && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted, marginTop: "8px" }}>Countersigned {fmt(f.countersigned_at)}</p>}
                  {f.pil_sent_at && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted, marginTop: "4px" }}>Sent to PIL {fmt(f.pil_sent_at)}</p>}
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "12px" }}>
                    {f.url && <GhostBtn href={f.url}>{f.countersigned_path ? "View full, internal (3 pp.) ↗" : "View PDF ↗"}</GhostBtn>}
                    {f.url && f.status !== "rejected" && <GhostBtn onClick={() => exportPil(f)}>{pilBusy === f.id ? "…" : "Export for PIL (pp. 1–2)"}</GhostBtn>}
                    {f.url && f.status === "approved" && <GhostBtn onClick={() => sendToPil(f)}>{pilBusy === f.id ? "Preparing…" : "Send to PIL →"}</GhostBtn>}
                    {f.status === "pending" && <GhostBtn onClick={() => setSigning(f)}>✍ Sign & approve</GhostBtn>}
                    {f.status === "pending" && <GhostBtn onClick={() => setStatus(f, "reject")} danger>Reject</GhostBtn>}
                    {f.status === "rejected" && <GhostBtn onClick={() => setStatus(f, "reopen")}>Reopen</GhostBtn>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {signing && (
        <CountersignModal form={signing} token={token} onClose={() => setSigning(null)} onDone={() => { setSigning(null); load(); }} />
      )}
    </div>
  );
}

function MerchPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | item object
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const empty = { name: "", artist: "", artist_slug: "", price: "", currency: "ILS", image_url: "", shopify_url: "", badge: "Limited Drop", locked_until: "", description: "", active: true, sold_out: false, sort_order: 0 };

  const [storeLocked, setStoreLocked] = useState(null);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from("merch").select("*").order("sort_order", { ascending: true });
    setItems(data || []);
    const { data: setting } = await supabase.from("site_settings").select("value").eq("key", "merch_locked").single();
    setStoreLocked(setting?.value === true || setting?.value === "true");
    setLoading(false);
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  async function toggleStoreLock() {
    const next = !storeLocked;
    await supabase.from("site_settings").upsert({ key: "merch_locked", value: next }, { onConflict: "key" });
    setStoreLocked(next);
  }

  async function save() {
    setSaving(true);
    const payload = { ...editing };
    delete payload.id;
    delete payload.created_at;
    if (payload.price === "") payload.price = null;
    if (payload.locked_until === "") payload.locked_until = null;
    if (editing.id) {
      await supabase.from("merch").update(payload).eq("id", editing.id);
    } else {
      await supabase.from("merch").insert(payload);
    }
    setSaving(false);
    setEditing(null);
    load();
  }

  async function del(id) {
    await supabase.from("merch").delete().eq("id", id);
    setDeleteId(null);
    load();
  }

  async function toggleActive(item) {
    await supabase.from("merch").update({ active: !item.active }).eq("id", item.id);
    load();
  }

  async function toggleSoldOut(item) {
    await supabase.from("merch").update({ sold_out: !item.sold_out }).eq("id", item.id);
    load();
  }

  const inputS = { width: "100%", boxSizing: "border-box", background: D.surface2, border: "1px solid " + D.border, color: D.text, fontFamily: F, fontSize: "12px", padding: "8px 12px", outline: "none", borderRadius: D.radiusSm };

  if (editing !== null) return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <FieldLabel>{editing.id ? "Edit Item" : "New Item"}</FieldLabel>
        <GhostBtn onClick={() => setEditing(null)}>Cancel</GhostBtn>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {[
          ["Name", "name", "text"],
          ["Artist", "artist", "text"],
          ["Price", "price", "number"],
          ["Currency", "currency", "text"],
          ["Image URL", "image_url", "text"],
          ["Shopify URL", "shopify_url", "text"],
          ["Artist Slug", "artist_slug", "text"],
          ["Badge", "badge", "text"],
          ["Drop Label (e.g. DROP 001)", "drop_label", "text"],
          ["Locked Until (YYYY-MM-DD)", "locked_until", "text"],
          ["Sort Order", "sort_order", "number"],
        ].map(([label, key, type]) => (
          <div key={key}>
            <FieldLabel>{label}</FieldLabel>
            <input type={type} value={editing[key] || ""} onChange={e => setEditing(p => ({ ...p, [key]: e.target.value }))} style={inputS} />
          </div>
        ))}
        <div>
          <FieldLabel>Tags (comma-separated, e.g. BOX FIT, HEAVY, UNISEX)</FieldLabel>
          <input
            type="text"
            value={Array.isArray(editing.tags) ? editing.tags.join(", ") : (editing.tags || "")}
            onChange={e => setEditing(p => ({ ...p, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) }))}
            placeholder="BOX FIT, HEAVY, UNISEX"
            style={inputS}
          />
        </div>
        <div>
          <FieldLabel>Description</FieldLabel>
          <textarea value={editing.description || ""} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} rows={3} style={{ ...inputS, resize: "vertical" }} />
        </div>
        <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
          <ActionBtn onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</ActionBtn>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <FieldLabel>{items.length} Items</FieldLabel>
        <div style={{ display: "flex", gap: "8px" }}>
          <GhostBtn onClick={toggleStoreLock} style={{ borderColor: storeLocked ? D.red : "rgba(74,222,128,0.4)", color: storeLocked ? D.red : "rgba(74,222,128,0.9)" }}>
            {storeLocked ? "Store: Locked" : "Store: Open"}
          </GhostBtn>
          <ActionBtn onClick={() => setEditing({ ...empty })}>+ Add Item</ActionBtn>
        </div>
      </div>
      {loading ? (
        <p style={{ fontFamily: F, fontSize: "11px", color: D.muted }}>Loading...</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {items.map(item => (
            <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 14px", background: D.surface, border: "1px solid " + D.border, borderRadius: D.radius }}>
              {item.image_url && <img src={item.image_url} alt="" style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "2px", flexShrink: 0, opacity: item.active ? 1 : 0.3 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: F, fontSize: "12px", fontWeight: 600, color: item.active ? D.text : D.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</p>
                <p style={{ fontFamily: F, fontSize: "10px", color: D.muted, marginTop: "2px" }}>{item.artist}{item.price ? " · " + item.price + " " + item.currency : ""}{item.locked_until ? " · Locked until " + item.locked_until : ""}</p>
              </div>
              <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                <GhostBtn onClick={() => toggleSoldOut(item)} style={{ borderColor: item.sold_out ? D.red : D.border, color: item.sold_out ? D.red : D.muted }}>
                  {item.sold_out ? "Sold Out" : "In Stock"}
                </GhostBtn>
                <GhostBtn onClick={() => toggleActive(item)}>{item.active ? "Hide" : "Show"}</GhostBtn>
                <GhostBtn onClick={() => setEditing({ ...item })}>Edit</GhostBtn>
                {deleteId === item.id ? (
                  <GhostBtn onClick={() => del(item.id)} style={{ color: D.red, borderColor: D.red }}>Confirm</GhostBtn>
                ) : (
                  <GhostBtn onClick={() => setDeleteId(item.id)}>Delete</GhostBtn>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   OVERVIEW PANEL — dashboard landing screen
══════════════════════════════════════════════ */
function OverviewPanel({ releases, artists, onNavigate }) {
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const upcoming = useMemo(() => {
    return releases
      .map(r => ({ ...r, effectiveDate: r.release_at || r.date }))
      .filter(r => r.effectiveDate)
      .filter(r => new Date(r.effectiveDate + "T00:00:00") >= today)
      .sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate));
  }, [releases, today]);

  const next = upcoming[0] || null;
  const daysUntil = next ? Math.round((new Date(next.effectiveDate + "T00:00:00") - today) / 86400000) : null;

  const recent = useMemo(() =>
    [...releases].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5)
  , [releases]);

  const thisYear = new Date().getFullYear();
  const releasesThisYear = releases.filter(r => (r.release_at || r.date || "").startsWith(String(thisYear))).length;

  const countdownLabel = daysUntil === 0 ? "Today" : daysUntil === 1 ? "Tomorrow" : daysUntil < 0 ? "Released" : `In ${daysUntil} days`;

  /* releases missing a cover, slug, or any streaming/smart link */
  const flagged = useMemo(() => releases.filter(r =>
    !r.cover || !r.slug || (!r.spotify_url && !r.apple_url && !r.youtube_url && !r.smart_link)
  ), [releases]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Next release countdown */}
      <div style={{
        display: "flex", alignItems: "center", gap: "18px", padding: "22px",
        background: next ? "rgba(74,222,128,0.06)" : D.surface2,
        border: `1px solid ${next ? "rgba(74,222,128,0.25)" : D.border}`,
        borderRadius: D.radius,
      }}>
        {next?.cover && (
          <img src={next.cover} alt="" style={{ width: "64px", height: "64px", borderRadius: D.radiusSm,
            objectFit: "cover", flexShrink: 0, border: `1px solid ${D.border}` }} />
        )}
        <div style={{ minWidth: 0 }}>
          <p style={{ fontFamily: F, fontSize: "10px", fontWeight: 600, letterSpacing: "0.1em",
            textTransform: "uppercase", color: D.muted, marginBottom: "6px" }}>Next Release</p>
          {next ? (
            <>
              <p style={{ fontFamily: F, fontSize: "20px", fontWeight: 700, color: D.text, lineHeight: 1.25 }}>
                {countdownLabel}
              </p>
              <p style={{ fontFamily: F, fontSize: "12px", color: D.muted, marginTop: "4px",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {next.artist} — {next.title} · {formatDateEU(next.effectiveDate)}
              </p>
            </>
          ) : (
            <p style={{ fontFamily: F, fontSize: "13px", color: D.muted }}>No upcoming releases scheduled.</p>
          )}
        </div>
      </div>

      {/* stat tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
        <StatTile label="Total Releases" value={releases.length} />
        <StatTile label="Artists" value={artists.length} />
        <StatTile label={`Released in ${thisYear}`} value={releasesThisYear} />
      </div>

      {/* needs attention */}
      {flagged.length > 0 && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px",
          padding: "12px 16px", background: "rgba(251,191,36,0.06)",
          border: "1px solid rgba(251,191,36,0.25)", borderRadius: D.radiusSm,
        }}>
          <p style={{ fontFamily: F, fontSize: "11px", color: D.text }}>
            <span style={{ color: "rgba(251,191,36,0.9)" }}>⚠ </span>
            {flagged.length} release{flagged.length === 1 ? "" : "s"} missing a cover, slug, or streaming link
          </p>
          <GhostBtn onClick={() => onNavigate("releases")}>Review</GhostBtn>
        </div>
      )}

      {/* recent releases */}
      <div>
        <FieldLabel>Recent Releases</FieldLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {recent.length === 0 && <p style={{ fontFamily: F, fontSize: "11px", color: D.muted, opacity: 0.5 }}>No releases yet.</p>}
          {recent.map(r => (
            <DataRow key={r.id} label={`${r.artist} — ${r.title}`} sub={formatDateEU(r.date)} />
          ))}
        </div>
      </div>

      {/* quick actions */}
      <div>
        <FieldLabel>Quick Actions</FieldLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
          <ActionBtn onClick={() => onNavigate("releases")} small>+ Add Release</ActionBtn>
          <ActionBtn onClick={() => onNavigate("artists")} small>+ Add Artist</ActionBtn>
          <ActionBtn onClick={() => onNavigate("submissions")} small>Submissions</ActionBtn>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════════════════ */
export default function AdminDashboard() {
  const [auth, setAuth] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [loginStep, setLoginStep] = useState("password"); // "password" | "totp"
  const [totpCode, setTotpCode] = useState("");
  const [totpSubmitting, setTotpSubmitting] = useState(false);

  /* data */
  const [releases, setReleases] = useState([]);
  const [artists, setArtists] = useState([]);
  const [posts, setPosts] = useState([]);

  /* active panel */
  const [activePanel, setActivePanel] = useState("overview");
  const [navOpen, setNavOpen] = useState(false); // mobile sidebar toggle
  const [paletteOpen, setPaletteOpen] = useState(false); // ⌘K command palette

  /* release form */
  const [releaseForm, setReleaseForm] = useState({
    title: "", artist: "", type: "Single", date: "", release_at: "",
    slug: "", cover: "", smart_link: "", spotify_url: "", apple_url: "",
    youtube_url: "", embed_youtube_id: "", embed_spotify: "",
    background_url: "", socials_instagram: "", socials_tiktok: "", socials_youtube: "", socials_website: "",
  });
  const [releaseStatus, setReleaseStatus] = useState(null);
  const [releaseSubmitting, setReleaseSubmitting] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null); // release object being edited
  const [configRelease, setConfigRelease] = useState(null); // release being page-configured
  const [prefilledFromSubmission, setPrefilledFromSubmission] = useState(false);

  /* artist form */
  const [artistForm, setArtistForm] = useState({ id: "", password: "", display_name: "", filter_name: "", upload_url: "", slug: "" });
  const [artistStatus, setArtistStatus] = useState(null);
  const [artistSubmitting, setArtistSubmitting] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);

  /* press form */
  const [postForm, setPostForm] = useState({ id: "", title: "", artist: "", cover_url: "", date: "", excerpt: "", body: "", slug: "", publish_at: "" });
  const [postStatus, setPostStatus] = useState(null);
  const [postSubmitting, setPostSubmitting] = useState(false);
  const [editingPost, setEditingPost] = useState(false);

  /* auth */
  useEffect(() => { if (localStorage.getItem("yenAdminAuthed") === "true") setAuth(true); }, []);

  /* ⌘K / Ctrl+K opens the command palette anywhere in the dashboard */
  useEffect(() => {
    if (!auth) return;
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    (async () => {
      const [rel, art, pst] = await Promise.all([
        supabase.from("releases").select("id,title,artist,date,release_at,slug,cover,spotify_url,apple_url,youtube_url,smart_link").order("date", { ascending: false }),
        supabase.from("artists").select("*").order("created_at", { ascending: true }),
        supabase.from("press_posts").select("*").order("date", { ascending: false }),
      ]);
      if (rel.data) setReleases(rel.data);
      if (art.data) setArtists(art.data);
      if (pst.data) setPosts(pst.data);
    })();
  }, [auth]);

  const handleLogin = () => {
    if (password === "sighmadethissite") { setAuthError(""); setLoginStep("totp"); }
    else setAuthError("Incorrect password");
  };

  const handleVerifyTotp = async () => {
    if (!totpCode.trim()) { setAuthError("Enter the 6-digit code"); return; }
    setTotpSubmitting(true); setAuthError("");
    try {
      const res = await fetch("/api/verify-totp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: totpCode.trim() }),
      });
      const json = await res.json();
      if (!res.ok) { setAuthError(json.message || "Invalid code"); setTotpSubmitting(false); return; }
      setAuth(true);
      localStorage.setItem("yenAdminAuthed", "true");
    } catch (e) {
      setAuthError("Network error verifying code");
    }
    setTotpSubmitting(false);
  };

  const handleBackToPassword = () => {
    setLoginStep("password"); setTotpCode(""); setAuthError("");
  };

  const handleLogout = () => {
    localStorage.removeItem("yenAdminAuthed");
    setAuth(false);
    setPassword("");
    setLoginStep("password");
    setTotpCode("");
    setAuthError("");
    setActivePanel("overview");
    setNavOpen(false);
  };

  const slugify = str => str.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 60);

  /* Jumps from a Submissions row straight into a pre-filled Add Release
     form. Note: slug is deliberately left blank — slugify() strips
     non-Latin characters, so it can't reliably build a slug from a
     Hebrew artist/title. Type falls back to "Single" if the submission's
     type string doesn't match one of the three release types exactly. */
  const handlePostSubmissionToReleases = sub => {
    const normalizedDate = normalizeToISODate(sub.releaseDate);
    const type = (sub.type === "Single" || sub.type === "Album" || sub.type === "EP") ? sub.type : "Single";
    setEditingRelease(null);
    setConfigRelease(null);
    setReleaseForm({
      title: sub.title || "", artist: sub.artist || "", type,
      date: normalizedDate, release_at: normalizedDate,
      slug: "", cover: sub.artwork || "",
      smart_link: "", spotify_url: "", apple_url: "", youtube_url: "",
      embed_youtube_id: "", embed_spotify: "", background_url: "",
      socials_instagram: "", socials_tiktok: "", socials_youtube: "", socials_website: "",
      internal_notes: buildInternalNotesFromSubmission(sub),
    });
    setReleaseStatus(null);
    setPrefilledFromSubmission(true);
    setActivePanel("releases");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ── Release handlers ── */
  const handleReleaseChange = e => {
    const { name, value } = e.target;
    const sanitized = name === "slug"
      ? value.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "").replace(/-+/g, "-")
      : value;
    setReleaseForm(p => ({ ...p, [name]: sanitized }));
  };
  const handleAddRelease = async () => {
    if (!releaseForm.title || !releaseForm.artist || !releaseForm.slug || !releaseForm.date) {
      setReleaseStatus("error:Title, Artist, Slug and Date are required."); return;
    }
    setReleaseSubmitting(true); setReleaseStatus(null);
    const cleanSlug = releaseForm.slug.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "").replace(/-+/g, "-");
    const { error } = await supabase.from("releases").insert([{
      title: releaseForm.title, artist: releaseForm.artist, type: releaseForm.type,
      date: releaseForm.date, release_at: releaseForm.release_at || null,
      slug: cleanSlug, cover: releaseForm.cover || null,
      smart_link: releaseForm.smart_link || null, spotify_url: releaseForm.spotify_url || null,
      apple_url: releaseForm.apple_url || null, youtube_url: releaseForm.youtube_url || null,
      embed_youtube_id: releaseForm.embed_youtube_id || null, embed_spotify: releaseForm.embed_spotify || null,
      background_url: releaseForm.background_url || null,
      internal_notes: releaseForm.internal_notes?.trim() || null,
      socials: {
        instagram: releaseForm.socials_instagram || "PLACEHOLDER",
        tiktok: releaseForm.socials_tiktok || "PLACEHOLDER",
        youtube: releaseForm.socials_youtube || "PLACEHOLDER",
        website: releaseForm.socials_website || "PLACEHOLDER",
      },
    }]);
    if (error) setReleaseStatus("error:" + error.message);
    else {
      setReleaseStatus("success");
      setPrefilledFromSubmission(false);
      setReleases(p => [{ id: Date.now(), title: releaseForm.title, artist: releaseForm.artist, date: releaseForm.date }, ...p]);
      setReleaseForm({ title: "", artist: "", type: "Single", date: "", release_at: "", slug: "", cover: "", smart_link: "", spotify_url: "", apple_url: "", youtube_url: "", embed_youtube_id: "", embed_spotify: "", background_url: "", socials_instagram: "", socials_tiktok: "", socials_youtube: "", socials_website: "", internal_notes: "" });
    }
    setReleaseSubmitting(false);
  };
  const handleDeleteRelease = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    const { error } = await supabase.from("releases").delete().eq("id", id);
    if (!error) setReleases(p => p.filter(r => r.id !== id));
    else alert(error.message);
  };
  const handleEditRelease = async (r) => {
    // fetch full release from Supabase to get all fields
    const { data } = await supabase.from("releases").select("*").eq("id", r.id).single();
    const d = data || r;
    setEditingRelease(d);
    setConfigRelease(null);
    setPrefilledFromSubmission(false);
    setReleaseForm({
      title: d.title || "", artist: d.artist || "", type: d.type || "Single",
      date: d.date || "", release_at: d.release_at || "",
      slug: d.slug || "", cover: d.cover || "",
      smart_link: d.smart_link || "", spotify_url: d.spotify_url || "",
      apple_url: d.apple_url || "", youtube_url: d.youtube_url || "",
      embed_youtube_id: d.embed_youtube_id || "", embed_spotify: d.embed_spotify || "",
      background_url: d.background_url || "",
      socials_instagram: d.socials?.instagram || "",
      socials_tiktok: d.socials?.tiktok || "",
      socials_youtube: d.socials?.youtube || "",
      socials_website: d.socials?.website || "",
      internal_notes: d.internal_notes || "",
    });
    setReleaseStatus(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleUpdateRelease = async () => {
    if (!editingRelease) return;
    setReleaseSubmitting(true); setReleaseStatus(null);
    const cleanSlug = releaseForm.slug.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "").replace(/-+/g, "-");
    const { error } = await supabase.from("releases").update({
      title: releaseForm.title, artist: releaseForm.artist, type: releaseForm.type,
      date: releaseForm.date, release_at: releaseForm.release_at || null,
      slug: cleanSlug, cover: releaseForm.cover || null,
      smart_link: releaseForm.smart_link || null, spotify_url: releaseForm.spotify_url || null,
      apple_url: releaseForm.apple_url || null, youtube_url: releaseForm.youtube_url || null,
      embed_youtube_id: releaseForm.embed_youtube_id || null, embed_spotify: releaseForm.embed_spotify || null,
      background_url: releaseForm.background_url || null,
      internal_notes: releaseForm.internal_notes?.trim() || null,
      socials: {
        instagram: releaseForm.socials_instagram || "PLACEHOLDER",
        tiktok: releaseForm.socials_tiktok || "PLACEHOLDER",
        youtube: releaseForm.socials_youtube || "PLACEHOLDER",
        website: releaseForm.socials_website || "PLACEHOLDER",
      },
    }).eq("id", editingRelease.id);
    if (error) setReleaseStatus("error:" + error.message);
    else {
      setReleaseStatus("success");
      setReleases(p => p.map(r => r.id === editingRelease.id ? { ...r, title: releaseForm.title, artist: releaseForm.artist, date: releaseForm.date } : r));
      setEditingRelease(null);
      setReleaseForm({ title: "", artist: "", type: "Single", date: "", release_at: "", slug: "", cover: "", smart_link: "", spotify_url: "", apple_url: "", youtube_url: "", embed_youtube_id: "", embed_spotify: "", background_url: "", socials_instagram: "", socials_tiktok: "", socials_youtube: "", socials_website: "", internal_notes: "" });
    }
    setReleaseSubmitting(false);
  };

  /* ── Artist handlers ── */
  const handleArtistChange = e => setArtistForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleAddArtist = async () => {
    if (!artistForm.id || !artistForm.password || !artistForm.display_name) {
      setArtistStatus("error:ID, Password and Display Name are required."); return;
    }
    setArtistSubmitting(true); setArtistStatus(null);
    const { error } = await supabase.from("artists").insert([{
      id: artistForm.id.trim(), password: artistForm.password.trim(),
      display_name: artistForm.display_name.trim(),
      filter_name: artistForm.filter_name.trim() || artistForm.display_name.trim(),
      upload_url: artistForm.upload_url.trim() || null,
      slug: artistForm.slug.trim() || null,
    }]);
    if (error) setArtistStatus("error:" + error.message);
    else { setArtistStatus("success"); setArtists(p => [...p, { ...artistForm }]); setArtistForm({ id: "", password: "", display_name: "", filter_name: "", upload_url: "", slug: "" }); }
    setArtistSubmitting(false);
  };
  const handleDeleteArtist = async (id) => {
    if (!window.confirm(`Delete artist "${id}"?`)) return;
    const { error } = await supabase.from("artists").delete().eq("id", id);
    if (!error) { setArtists(p => p.filter(a => a.id !== id)); if (editingArtist?.id === id) { setEditingArtist(null); setArtistForm({ id: "", password: "", display_name: "", filter_name: "", upload_url: "", slug: "" }); } }
    else alert(error.message);
  };
  const handleEditArtist = (a) => {
    setEditingArtist(a);
    setArtistForm({ id: a.id || "", password: a.password || "", display_name: a.display_name || "", filter_name: a.filter_name || "", upload_url: a.upload_url || "", slug: a.slug || "" });
    setArtistStatus(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleUpdateArtist = async () => {
    if (!editingArtist) return;
    setArtistSubmitting(true); setArtistStatus(null);
    const { error } = await supabase.from("artists").update({
      password: artistForm.password.trim(),
      display_name: artistForm.display_name.trim(),
      filter_name: artistForm.filter_name.trim() || artistForm.display_name.trim(),
      upload_url: artistForm.upload_url.trim() || null,
      slug: artistForm.slug.trim() || null,
    }).eq("id", editingArtist.id);
    if (error) setArtistStatus("error:" + error.message);
    else {
      setArtistStatus("success");
      setArtists(p => p.map(a => a.id === editingArtist.id ? { ...a, ...artistForm, slug: artistForm.slug.trim() || null } : a));
      setEditingArtist(null);
      setArtistForm({ id: "", password: "", display_name: "", filter_name: "", upload_url: "", slug: "" });
    }
    setArtistSubmitting(false);
  };

  /* ── Press handlers ── */
  const handlePostChange = e => setPostForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleSavePost = async () => {
    const editorEl = document.querySelector("[data-yen-editor]");
    const finalBody = (editorEl ? editorEl.innerHTML : postForm.body).trim();
    if (!postForm.title || !finalBody) { setPostStatus("error:Title and body are required."); return; }
    setPostSubmitting(true); setPostStatus(null);
    const payload = {
      title: postForm.title.trim(), artist: postForm.artist.trim() || null,
      cover_url: postForm.cover_url.trim() || null,
      date: postForm.date || new Date().toISOString().slice(0, 10),
      excerpt: postForm.excerpt.trim() || null,
      body: finalBody,
      slug: postForm.slug.trim() || slugify(postForm.title),
      publish_at: postForm.publish_at?.trim() || null,
    };
    let err;
    if (editingPost && postForm.id) {
      ({ error: err } = await supabase.from("press_posts").update(payload).eq("id", postForm.id));
      if (!err) setPosts(p => p.map(x => x.id === postForm.id ? { ...x, ...payload } : x));
    } else {
      ({ error: err } = await supabase.from("press_posts").insert([payload]));
      if (!err) setPosts(p => [{ ...payload, id: Date.now() }, ...p]);
    }
    if (err) setPostStatus("error:" + err.message);
    else { setPostStatus("success"); setPostForm({ id: "", title: "", artist: "", cover_url: "", date: "", excerpt: "", body: "", slug: "", publish_at: "" }); setEditingPost(false); }
    setPostSubmitting(false);
  };
  const handleEditPost = post => {
    setEditingPost(true);
    setPostForm({ id: post.id, title: post.title || "", artist: post.artist || "", cover_url: post.cover_url || "", date: post.date || "", excerpt: post.excerpt || "", body: post.body || "", slug: post.slug || "", publish_at: post.publish_at || "" });
    setPostStatus(null);
    setActivePanel("press");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleDeletePost = async (id, title) => {
    if (!window.confirm(`Delete post "${title}"?`)) return;
    const { error } = await supabase.from("press_posts").delete().eq("id", id);
    if (!error) setPosts(p => p.filter(x => x.id !== id));
    else alert(error.message);
  };

  /* ── Login screen ── */
  if (!auth) return (
    <div style={{ background: D.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div style={{ width: "100%", maxWidth: "340px" }}>
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <img src="/spinning yen logo white.gif" alt="YEN SOUND" style={{ width: "48px", height: "48px", opacity: 0.6, marginBottom: "16px" }} />
          <p style={{ fontFamily: F, fontSize: "11px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: D.muted }}>Admin Dashboard</p>
        </div>
        <div style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: D.radius, padding: "28px", boxShadow: D.shadowLg }}>
          {loginStep === "password" ? (
            <>
              <FieldLabel>Password</FieldLabel>
              <Input value={password} onChange={setPassword} placeholder="Enter password" type="password" />
              {authError && (
                <p style={{ fontFamily: F, fontSize: "12px", color: D.red, marginTop: "8px" }}>{authError}</p>
              )}
              <div style={{ marginTop: "16px" }}>
                <ActionBtn onClick={handleLogin}>Enter</ActionBtn>
              </div>
            </>
          ) : (
            <>
              <FieldLabel>Authenticator Code</FieldLabel>
              <p style={{ fontFamily: F, fontSize: "10px", color: D.muted, marginBottom: "12px", lineHeight: 1.6 }}>
                Enter the 6-digit code from Google Authenticator.
              </p>
              <Input
                value={totpCode}
                onChange={v => setTotpCode(v.replace(/[^0-9]/g, "").slice(0, 6))}
                placeholder="000000"
                type="text"
              />
              {authError && (
                <p style={{ fontFamily: F, fontSize: "12px", color: D.red, marginTop: "8px" }}>{authError}</p>
              )}
              <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                <ActionBtn onClick={handleVerifyTotp} disabled={totpSubmitting}>
                  {totpSubmitting ? "Verifying..." : "Verify"}
                </ActionBtn>
                <GhostBtn onClick={handleBackToPassword}>Back</GhostBtn>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const tiles = [
    { id: "overview",   icon: "⌂", label: "Overview",   pin: true },
    { id: "releases",   icon: "♫", label: "Releases"   },
    { id: "artists",    icon: "◈", label: "Artists"    },
    { id: "press",      icon: "✎", label: "Press"      },
    { id: "slugs",      icon: "⌘", label: "Slugs"      },
    { id: "photos",     icon: "◻", label: "Photos"     },
    { id: "royalties",  icon: "₪", label: "Royalties"  },
    { id: "analytics",  icon: "◎", label: "Analytics"  },
    { id: "submissions", icon: "↓", label: "Submissions" },
    { id: "forms",       icon: "✍", label: "Signed Forms" },
    { id: "merch",       icon: "◑", label: "Merch"       },
    { id: "activity",    icon: "◉", label: "Activity"    },
  ];

  const toggle = id => { setActivePanel(p => p === id ? null : id); setNavOpen(false); };
  const navigate = id => { setActivePanel(id); setNavOpen(false); };

  return (
    <div style={{ background: D.bg, minHeight: "100vh", color: D.text, display: "flex" }} className="admin-shell">
      <style>{`
        .admin-mobile-header { display: none; }
        .admin-mobile-overlay { display: none; }
        @media (max-width: 860px) {
          .admin-shell { display: block !important; }
          .admin-sidebar {
            position: fixed; left: 0; top: 0; height: 100vh; z-index: 60;
            transform: translateX(-100%); transition: transform 0.2s ease;
          }
          .admin-sidebar.open { transform: translateX(0); }
          .admin-mobile-header { display: flex; }
          .admin-main { padding-top: 0 !important; }
        }
        .admin-mobile-overlay.open { display: block; }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        @media (max-width: 480px) {
          .form-grid-2 { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* mobile top bar */}
      <div className="admin-mobile-header" style={{ alignItems: "center", gap: "12px", padding: "16px 20px",
        borderBottom: `1px solid ${D.border}`, background: D.surface, position: "sticky", top: 0, zIndex: 30 }}>
        <button onClick={() => setNavOpen(true)} aria-label="Open menu" style={{
          background: "transparent", border: `1px solid ${D.border}`, borderRadius: D.radiusSm,
          color: D.text, width: "34px", height: "34px", fontSize: "16px", cursor: "pointer",
        }}>☰</button>
        <img src="/spinning yen logo white.gif" alt="YEN SOUND" style={{ width: "22px", height: "22px", opacity: 0.6 }} />
        <span style={{ fontFamily: F, fontSize: "12px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>Admin</span>
      </div>

      {/* mobile overlay */}
      <div className={`admin-mobile-overlay${navOpen ? " open" : ""}`} onClick={() => setNavOpen(false)} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 55,
      }} />

      {/* sidebar */}
      <aside className={`admin-sidebar${navOpen ? " open" : ""}`} style={{
        width: "230px", flexShrink: 0, height: "100vh", position: "sticky", top: 0,
        overflowY: "auto", background: D.surface, borderRight: `1px solid ${D.border}`,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "24px 18px 20px", borderBottom: `1px solid ${D.border}` }}>
          <img src="/spinning yen logo white.gif" alt="YEN SOUND" className="yen-spin" style={{ width: "28px", height: "28px", opacity: 0.6 }} />
          <div>
            <h1 style={{ fontFamily: F, fontSize: "13px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: D.text, lineHeight: 1 }}>Admin</h1>
            <p style={{ fontFamily: F, fontSize: "10px", color: D.muted, marginTop: "3px" }}>Yen Sound Dashboard</p>
          </div>
        </div>

        <div style={{ padding: "14px 12px 0" }}>
          <button onClick={() => setPaletteOpen(true)} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
            padding: "9px 12px", boxSizing: "border-box", background: D.surface2, border: `1px solid ${D.border}`,
            borderRadius: D.radiusSm, cursor: "pointer", color: D.muted, fontFamily: F, fontSize: "11px",
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = D.borderHi}
            onMouseLeave={e => e.currentTarget.style.borderColor = D.border}
          >
            <span>Search...</span>
            <span style={{ fontSize: "10px", opacity: 0.6 }}>⌘K</span>
          </button>
        </div>

        <nav style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "10px 12px 14px", flex: 1 }}>
          {tiles.map(t => <SidebarNavItem key={t.id} icon={t.icon} label={t.label} active={activePanel === t.id} onClick={() => t.pin ? navigate(t.id) : toggle(t.id)} />)}
        </nav>

        <div style={{ padding: "14px 12px", borderTop: `1px solid ${D.border}` }}>
          <GhostBtn onClick={handleLogout} danger>Log Out</GhostBtn>
        </div>
      </aside>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        tiles={tiles}
        releases={releases}
        artists={artists}
        onNavigate={navigate}
        onEditRelease={r => { handleEditRelease(r); navigate("releases"); }}
        onEditArtist={a => { handleEditArtist(a); navigate("artists"); }}
      />

      {/* main content */}
      <main className="admin-main" style={{ flex: 1, minWidth: 0 }}>
        <div style={{ maxWidth: "640px", padding: "32px 24px 80px" }}>

        {/* ── Overview ── */}
        {activePanel === "overview" && (
          <Panel>
            <OverviewPanel releases={releases} artists={artists} onNavigate={navigate} />
          </Panel>
        )}

        {/* ── Releases ── */}
        {activePanel === "releases" && (
          <>
            <Panel>
              <FieldLabel>{editingRelease ? `Editing: ${editingRelease.title}` : prefilledFromSubmission ? "Add Release — pre-filled from submission" : "Add Release"}</FieldLabel>
              {prefilledFromSubmission && !editingRelease && (
                <p style={{ fontFamily: F, fontSize: "10px", color: "rgba(74,222,128,0.8)", marginBottom: "12px", lineHeight: 1.6, letterSpacing: "0.02em" }}>
                  Pulled in from the submission — double-check everything, and note the Slug is left blank on purpose (auto-generation doesn't handle Hebrew titles reliably).
                </p>
              )}
              <div>
                {/* Basics */}
                <div className="form-grid-2">
                  <NamedInput name="title" value={releaseForm.title} onChange={handleReleaseChange} placeholder="Title *" />
                  <NamedInput name="artist" value={releaseForm.artist} onChange={handleReleaseChange} placeholder="Artist *" />
                </div>
                <div className="form-grid-2" style={{ marginTop: "8px" }}>
                  <select name="type" value={releaseForm.type} onChange={handleReleaseChange}
                    style={{ background: D.surface2, border: `1px solid ${D.border}`, color: D.text, fontFamily: F, fontSize: "13px", padding: "10px 14px", cursor: "pointer", outline: "none", width: "100%", boxSizing: "border-box", borderRadius: D.radiusSm }}>
                    <option value="Single">Single</option>
                    <option value="Album">Album</option>
                    <option value="EP">EP</option>
                  </select>
                  <NamedInput name="slug" value={releaseForm.slug} onChange={handleReleaseChange} placeholder="Slug * (e.g. bahaimhaele)" />
                </div>
                <div className="form-grid-2" style={{ marginTop: "8px" }}>
                  <NamedInput name="date" value={releaseForm.date} onChange={handleReleaseChange} placeholder="Date * (YYYY-MM-DD)" />
                  <NamedInput name="release_at" value={releaseForm.release_at} onChange={handleReleaseChange} placeholder="Release At (countdown)" />
                </div>

                <FormSection title="Cover Image">
                  <CoverUploadField
                    value={releaseForm.cover}
                    onChange={v => setReleaseForm(p => ({ ...p, cover: v }))}
                  />
                </FormSection>

                <FormSection title="Streaming & Links" hint="Where fans can listen or pre-save.">
                  <NamedInput name="smart_link" value={releaseForm.smart_link} onChange={handleReleaseChange} placeholder="Smart Link URL" />
                  <div className="form-grid-2">
                    <NamedInput name="spotify_url" value={releaseForm.spotify_url} onChange={handleReleaseChange} placeholder="Spotify URL" />
                    <NamedInput name="apple_url" value={releaseForm.apple_url} onChange={handleReleaseChange} placeholder="Apple Music URL" />
                  </div>
                  <NamedInput name="youtube_url" value={releaseForm.youtube_url} onChange={handleReleaseChange} placeholder="YouTube URL" />
                </FormSection>

                <FormSection title="Embeds & Background" hint="Optional — used on the release page for the audio player and backdrop.">
                  <div className="form-grid-2">
                    <NamedInput name="embed_youtube_id" value={releaseForm.embed_youtube_id} onChange={handleReleaseChange} placeholder="YouTube Embed ID" />
                    <NamedInput name="embed_spotify" value={releaseForm.embed_spotify} onChange={handleReleaseChange} placeholder="Spotify Embed URL" />
                  </div>
                  <NamedInput name="background_url" value={releaseForm.background_url} onChange={handleReleaseChange} placeholder="Background image URL" />
                </FormSection>

                <FormSection title="Socials" hint="Shown as buttons on the release page.">
                  <div className="form-grid-2">
                    <NamedInput name="socials_instagram" value={releaseForm.socials_instagram} onChange={handleReleaseChange} placeholder="Instagram URL" />
                    <NamedInput name="socials_tiktok" value={releaseForm.socials_tiktok} onChange={handleReleaseChange} placeholder="TikTok URL" />
                  </div>
                  <div className="form-grid-2">
                    <NamedInput name="socials_youtube" value={releaseForm.socials_youtube} onChange={handleReleaseChange} placeholder="YouTube channel URL" />
                    <NamedInput name="socials_website" value={releaseForm.socials_website} onChange={handleReleaseChange} placeholder="Website URL" />
                  </div>
                </FormSection>

                <FormSection title="Internal Notes" hint="Admin only — never shown publicly.">
                  <textarea name="internal_notes" value={releaseForm.internal_notes || ""} onChange={handleReleaseChange} rows={2}
                    placeholder="e.g. waiting on artwork, DSP pitch sent, pre-save goes live 14/5..."
                    style={{ width: "100%", boxSizing: "border-box", background: D.surface2, border: `1px solid rgba(74,222,128,0.2)`, color: D.text, fontFamily: F, fontSize: "12px", padding: "10px 14px", outline: "none", borderRadius: D.radiusSm, resize: "vertical" }} />
                </FormSection>

                <div style={{ display: "flex", gap: "8px", marginTop: "18px" }}>
                  <ActionBtn onClick={editingRelease ? handleUpdateRelease : handleAddRelease} disabled={releaseSubmitting}>
                    {releaseSubmitting ? "Saving..." : editingRelease ? "Update Release" : "Add Release"}
                  </ActionBtn>
                  {(editingRelease || prefilledFromSubmission) && (
                    <ActionBtn onClick={() => {
                      setEditingRelease(null);
                      setPrefilledFromSubmission(false);
                      setReleaseForm({ title: "", artist: "", type: "Single", date: "", release_at: "", slug: "", cover: "", smart_link: "", spotify_url: "", apple_url: "", youtube_url: "", embed_youtube_id: "", embed_spotify: "", background_url: "", socials_instagram: "", socials_tiktok: "", socials_youtube: "", socials_website: "", internal_notes: "" });
                      setReleaseStatus(null);
                    }}>Cancel</ActionBtn>
                  )}
                </div>
                <StatusMsg status={releaseStatus} noun={editingRelease ? "Release updated" : "Release added"} />
              </div>
            </Panel>

            {configRelease && (
              <Panel>
                <FieldLabel>Page Config — {releases.find(r => r.id === configRelease.id)?.title || "Release"}</FieldLabel>
                <ReleasePageConfig
                  key={configRelease.id}
                  releaseId={configRelease.id}
                  initialButtons={configRelease.custom_buttons || []}
                  initialOrder={configRelease.button_order || []}
                  initialEmbedUrl={configRelease.embed_url || ""}
                  onSaved={() => setConfigRelease(null)}
                />
                <div style={{ marginTop: "12px" }}>
                  <GhostBtn onClick={() => setConfigRelease(null)}>Cancel</GhostBtn>
                </div>
              </Panel>
            )}

            <Panel>
              <FieldLabel>All Releases · {releases.length}</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {releases.map(r => {
                  const missing = [];
                  if (!r.cover) missing.push("cover");
                  if (!r.slug) missing.push("slug");
                  if (!r.spotify_url && !r.apple_url && !r.youtube_url && !r.smart_link) missing.push("links");
                  const flagged = missing.length > 0;
                  return (
                    <DataRow
                      key={r.id}
                      label={flagged ? <><span style={{ color: "rgba(251,191,36,0.9)" }}>⚠ </span>{r.title}</> : r.title}
                      sub={flagged ? `${r.artist} · ${r.date} · missing ${missing.join(", ")}` : `${r.artist} · ${r.date}`}
                    >
                      <GhostBtn href={`/release/${r.slug || r.id}`}>View</GhostBtn>
                      <GhostBtn onClick={() => { handleEditRelease(r); setActivePanel("releases"); }}>Edit</GhostBtn>
                      <GhostBtn onClick={async () => {
                        const { data } = await supabase.from("releases").select("id,custom_buttons,button_order,embed_url").eq("id", r.id).single();
                        setConfigRelease(data || r);
                        setEditingRelease(null);
                        setActivePanel("releases");
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}>Page</GhostBtn>
                      <GhostBtn danger onClick={() => handleDeleteRelease(r.id, r.title)}>Delete</GhostBtn>
                    </DataRow>
                  );
                })}
              </div>
            </Panel>
          </>
        )}

        {/* ── Artists ── */}
        {activePanel === "artists" && (
          <>
            <Panel>
              <FieldLabel>{editingArtist ? `Editing: ${editingArtist.display_name}` : "Add Artist Login"}</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {[
                  { name: "id",           placeholder: "Artist ID * (e.g. sigh)",               disabled: !!editingArtist },
                  { name: "password",     placeholder: "Password *"                                                        },
                  { name: "display_name", placeholder: "Display Name *"                                                    },
                  { name: "filter_name",  placeholder: "Filter Name (exact name in releases)"                              },
                  { name: "slug",         placeholder: "Slug (e.g. sighdafekt — for /artist/…)"                           },
                  { name: "upload_url",   placeholder: "Google Drive Vault URL"                                            },
                ].map(f => <NamedInput key={f.name} name={f.name} value={artistForm[f.name]} onChange={f.disabled ? undefined : handleArtistChange} placeholder={f.placeholder} style={f.disabled ? { opacity: 0.4, pointerEvents: "none" } : undefined} />)}
                <div style={{ display: "flex", gap: "8px" }}>
                  <ActionBtn onClick={editingArtist ? handleUpdateArtist : handleAddArtist} disabled={artistSubmitting}>
                    {artistSubmitting ? "Saving..." : editingArtist ? "Update Artist" : "Add Artist"}
                  </ActionBtn>
                  {editingArtist && (
                    <ActionBtn onClick={() => { setEditingArtist(null); setArtistForm({ id: "", password: "", display_name: "", filter_name: "", upload_url: "", slug: "" }); setArtistStatus(null); }}>Cancel</ActionBtn>
                  )}
                </div>
                <StatusMsg status={artistStatus} noun={editingArtist ? "Artist updated" : "Artist added"} />
              </div>
            </Panel>

            <Panel>
              <FieldLabel>All Artists · {artists.length}</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {artists.map(a => (
                  <DataRow key={a.id} label={a.display_name} sub={`ID: ${a.id}${a.slug ? ` · /artist/${a.slug}` : " · no slug"}`}>
                    {a.slug && <GhostBtn href={`/artist/${a.slug}`}>View</GhostBtn>}
                    <GhostBtn onClick={() => handleEditArtist(a)}>Edit</GhostBtn>
                    <GhostBtn danger onClick={() => handleDeleteArtist(a.id)}>Delete</GhostBtn>
                  </DataRow>
                ))}
              </div>
            </Panel>
          </>
        )}

        {/* ── Press ── */}
        {activePanel === "press" && (
          <>
            <Panel>
              <FieldLabel>{editingPost ? "Edit Post" : "New Press Post"}</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  <NamedInput name="title" value={postForm.title} onChange={handlePostChange} placeholder="Title *" />
                  <NamedInput name="artist" value={postForm.artist} onChange={handlePostChange} placeholder="Artist tag" />
                  <NamedInput name="date" value={postForm.date} onChange={handlePostChange} placeholder="Date (YYYY-MM-DD)" />
                  <NamedInput name="slug" value={postForm.slug} onChange={handlePostChange} placeholder="Slug (auto if blank)" />
                  <NamedInput name="cover_url" value={postForm.cover_url} onChange={handlePostChange} placeholder="Cover image URL" />
                  <NamedInput name="excerpt" value={postForm.excerpt} onChange={handlePostChange} placeholder="Excerpt / subtitle" />
                  <div style={{ gridColumn: "1 / -1" }}>
                    <FieldLabel>Publish Date (leave blank to publish immediately)</FieldLabel>
                    <NamedInput name="publish_at" value={postForm.publish_at || ""} onChange={handlePostChange} placeholder="YYYY-MM-DD (schedule for future)" />
                    {postForm.publish_at && new Date(postForm.publish_at + "T00:00:00") > new Date() && (
                      <p style={{ fontFamily: F, fontSize: "9px", color: "rgba(74,222,128,0.7)", marginTop: "6px", letterSpacing: "0.06em" }}>
                        Scheduled · will go live on {new Date(postForm.publish_at + "T00:00:00").toLocaleDateString("he-IL")}
                      </p>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: "8px" }}>
                  <FieldLabel>Body</FieldLabel>
                  <RichEditor key={editingPost ? postForm.id : "new"} value={postForm.body} onChange={v => setPostForm(p => ({ ...p, body: v }))} />
                </div>
                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <ActionBtn onClick={handleSavePost} disabled={postSubmitting}>
                    {postSubmitting ? "Saving..." : editingPost ? "Update Post" : "Publish Post"}
                  </ActionBtn>
                  {editingPost && (
                    <ActionBtn onClick={() => { setEditingPost(false); setPostForm({ id: "", title: "", artist: "", cover_url: "", date: "", excerpt: "", body: "", slug: "", publish_at: "" }); setPostStatus(null); }}>
                      Cancel
                    </ActionBtn>
                  )}
                </div>
                <StatusMsg status={postStatus} noun={editingPost ? "Post updated" : "Post published"} />
              </div>
            </Panel>

            <Panel>
              <FieldLabel>All Posts · {posts.length}</FieldLabel>
              {posts.length === 0 && <p style={{ fontFamily: F, fontSize: "10px", opacity: 0.25 }}>No posts yet.</p>}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {posts.map(p => (
                  <DataRow key={p.id} label={p.title} sub={`${p.artist || "—"} · ${p.date}`}>
                    <GhostBtn href={`/press/${p.slug}`}>View</GhostBtn>
                    <GhostBtn onClick={() => handleEditPost(p)}>Edit</GhostBtn>
                    <GhostBtn danger onClick={() => handleDeletePost(p.id, p.title)}>Delete</GhostBtn>
                  </DataRow>
                ))}
              </div>
            </Panel>
          </>
        )}

        {/* ── Slugs ── */}
        {activePanel === "slugs" && (
          <Panel>
            <SlugManager artists={artists} />
          </Panel>
        )}

        {/* ── Photos ── */}
        {activePanel === "photos" && (
          <Panel>
            <PhotosPanel artists={artists} onUpdate={updated => setArtists(p => p.map(a => a.id === updated.id ? { ...a, profile_image: updated.profile_image } : a))} />
          </Panel>
        )}

        {/* ── Submissions ── */}
        {activePanel === "submissions" && (
          <Panel>
            <SubmissionsPanel onPostRelease={handlePostSubmissionToReleases} />
          </Panel>
        )}

        {/* ── Signed Forms ── */}
        {activePanel === "forms" && (
          <Panel>
            <SignedFormsPanel />
          </Panel>
        )}

        {/* ── Merch ── */}
        {activePanel === "merch" && (
          <Panel>
            <MerchPanel />
          </Panel>
        )}

        {/* ── Analytics ── */}
        {activePanel === "analytics" && (
          <Panel>
            <AnalyticsPanel releases={releases} />
          </Panel>
        )}

        {/* ── Royalties ── */}
        {activePanel === "royalties" && (
          <Panel>
            <RoyaltiesPanel />
          </Panel>
        )}

        {/* ── Activity Log ── */}
        {activePanel === "activity" && (
          <Panel>
            <ActivityLogPanel />
          </Panel>
        )}
        </div>
      </main>
    </div>
  );
}
